import type {
  PlanningPageQuery,
  ScenarioTimeline_OrderLocationFragment,
} from '../common/graphql/types';
import type * as t from '../common/graphql/types';
import { mapGroupBy, mapKeyBy, mapKeyBy2 } from '../common/lib/collections';

const constantCaseAll = (str: string): string =>
  str
    .replace(/[^a-zA-Z0-9_]+/g, '_')
    .replace(/[A-Z]+/g, '_$&')
    .replace(/__+/g, '_')
    .replace(/^[^a-zA-Z0-9]+/, '')
    .replace(/^[0-9]/, '_$&') // GraphQL enums must not start with a number
    .toUpperCase();

const formatInsideUnderscores =
  (fn: (input: string) => string) =>
  (str: string): string => {
    const matches = str.match(/^(_*)([\s\S]*?)(_*)$/);
    if (!matches) {
      throw new Error('Impossible?'); // Satiate Flow
    }
    const [, start, middle, end] = matches;
    return `${start}${fn(middle)}${end}`;
  };

const constantCase = formatInsideUnderscores(constantCaseAll);

function enumMapper<
  E extends {
    [k: string]: string | number;
    [nu: number]: string;
  },
>(Enum: E) {
  function mapEnum<T extends null>(value: T): null;
  function mapEnum(value: string): E[keyof E];
  function mapEnum(value: string | null): E[keyof E] | null;
  function mapEnum(value: string | null): E[keyof E] | null {
    if (value === null) {
      return null;
    }
    const graphile = constantCase(value);
    const typescriptValue = Enum[graphile as keyof E];
    if (!typescriptValue) {
      throw new Error(`Unsupported enum value: ${value} ${graphile} ${Enum}`);
    }
    return typescriptValue;
  }
  return mapEnum;
}

import {
  CargoType,
  LoadingUnit,
  MarginUnit,
  Currency,
  HoldType,
  VolumeUnits,
  PaymentUnit,
  BafTypes,
  BunkerGrade,
  DocumentType,
  OffHireType,
  CargoDeckOptionType,
  ImoClass,
  IncoTermsType,
  CommissionType,
  ShipmentStatus,
  EventType,
  VesselArea,
  VesselSpeedTag,
  VesselSpeedType,
  VoyageCharterCostType,
  FreightType,
  VoyageNamingYearFormat,
  WeightUnits,
  WeightCapacityType,
  ChannelCostCargoCondition,
  PaymentTrigger,
  PaymentType,
} from '../common/graphql/types';
const mapCargoType = enumMapper(CargoType);
const mapLoadingUnit = enumMapper(LoadingUnit);
const mapMarginUnit = enumMapper(MarginUnit);
const mapCurrency = enumMapper(Currency);
const mapHoldType = enumMapper(HoldType);
const mapVolumeUnits = enumMapper(VolumeUnits);
const mapPaymentUnit = enumMapper(PaymentUnit);
const mapBafTypes = enumMapper(BafTypes);
const mapBunkerGrade = enumMapper(BunkerGrade);
const mapDocumentType = enumMapper(DocumentType);
const mapOffHireType = enumMapper(OffHireType);
const mapCargoDeckOptionType = enumMapper(CargoDeckOptionType);
const mapImoClass = enumMapper(ImoClass);
const mapIncoTermsType = enumMapper(IncoTermsType);
const mapCommissionType = enumMapper(CommissionType);
const mapShipmentStatus = enumMapper(ShipmentStatus);
const mapEventType = enumMapper(EventType);
const mapVesselArea = enumMapper(VesselArea);
const mapVesselSpeedTag = enumMapper(VesselSpeedTag);
const mapVesselSpeedType = enumMapper(VesselSpeedType);
const mapVoyageCharterCostType = enumMapper(VoyageCharterCostType);
const mapFreightType = enumMapper(FreightType);
const mapVoyageNamingYearFormat = enumMapper(VoyageNamingYearFormat);
const mapWeightUnits = enumMapper(WeightUnits);
const mapWeightCapacityType = enumMapper(WeightCapacityType);
const mapChannelCostCargoCondition = enumMapper(ChannelCostCargoCondition);
const mapPaymentTrigger = enumMapper(PaymentTrigger);
const mapPaymentType = enumMapper(PaymentType);

import { IncludedExcludedUnlessUsed } from '../common/graphql/types';
import type { ShincShex } from '../common/graphql/types';

const mapIncludedExcludedUnlessUsed = enumMapper(IncludedExcludedUnlessUsed);

function mapShincShex(laytime_days: string): Required<ShincShex>;
function mapShincShex(laytime_days: string | null): Required<ShincShex> | null;
function mapShincShex(laytime_days: string | null): Required<ShincShex> | null {
  if (laytime_days === null) {
    return null;
  }

  const { inc_ex_uu, fr, ho, mo, sa, su, th, tu, we } =
    JSON.parse(laytime_days);

  return {
    __typename: 'ShincShex',
    fr: fr,
    ho: ho,
    incExUu: mapIncludedExcludedUnlessUsed(inc_ex_uu),
    mo: mo,
    sa: sa,
    su: su,
    th: th,
    tu: tu,
    we: we,
  };
}

interface ApiOrdersNotices {
  id: number;
  notices: string;
  order_id: number | null;
}

interface AssortmentGroups {
  external_id: string | null;
  id: number;
  name: string;
}

interface Assortments {
  assortment_group: number | null;
  cargo_type: string | null;
  discharging_rate: string | null;
  discharging_unit: string | null;
  fill_factor: string | null;
  id: number;
  loading_rate: string | null;
  loading_unit: string | null;
  margin_amount: string | null;
  margin_unit: string | null;
  name: string;
  specific_gravity: string | null;
  stowage_factor_max: string | null;
  stowage_factor_min: string | null;
}

interface BunkerGradeOverride {
  carbon_factor: string | null;
  currency: string;
  grade_name: string;
  id: number;
  price: string | null;
  price_adjustment: string | null;
  reference_grade: string | null;
  reference_port: string | null;
}

interface CargoCompartment {
  id: number;
  max_volume: string;
  name: string;
  ship_id: number | null;
  type: string | null;
  vessel_id: number | null;
  volume_unit: string | null;
}

interface CargoCompartmentAllocation {
  amount: string;
  client_id: string;
  compartment_id: number;
  id: number;
  order_id: number;
  scheduled_order_client_id: string;
}

interface CargoExclusion {
  assortment: number | null;
  assortment_group: number | null;
  distance: string | null;
  id: number;
  order_id: number;
  tanks: number | null;
  voyages: number | null;
}

interface CargoUnits {
  id: number;
  name: string | null;
}

interface ChannelCostEstimates {
  assortment_group_id: number | null;
  cargo_condition: string | null;
  channel_cost: string;
  channel_cost_currency: string;
  channel_name: string;
  duration_overhead: string | null;
  id: number;
  vessel_id: number | null;
  vessel_type_id: number | null;
}

interface ChannelIncompatibilities {
  channel_name: string;
  id: number;
  vessel_attribute_id: number;
}

interface CoaContractAssortments {
  assortment_group_id: number | null;
  assortment_id: number | null;
  coa_contract_id: number;
  id: number;
  max_amount: string | null;
  min_amount: string | null;
  payment_unit: string;
}

interface CoaContractBunker {
  baf_bunker_amount: string | null;
  baf_start_from_base: boolean | null;
  baf_step_effect: string | null;
  baf_step_size: string | null;
  baf_type: string | null;
  baf_use_step_effect_as_value: boolean | null;
  base_price_max: string | null;
  bunker_grade: string;
  coa_assortment_id: number | null;
  coa_contract_id: number;
  coa_route_id: number | null;
  id: number;
  price: string;
}

interface CoaContractDeviationCosts {
  coa_contract_id: number;
  from_location_id: number | null;
  from_zone_id: number | null;
  id: number;
  lump_sum_amount: string | null;
  to_location_id: number | null;
  to_zone_id: number | null;
}

interface CoaContractPortData {
  coa_contract_id: number;
  id: number;
  port_data_id: number;
}

interface CoaContractPorts {
  coa_contract_id: number;
  destination_id: number | null;
  destination_zone_id: number | null;
  id: number;
  origin_id: number | null;
  origin_zone_id: number | null;
}

interface CoaContractPrices {
  coa_assortment_id: number;
  coa_contract_id: number;
  id: number;
  price: string;
  route_id: number | null;
}

interface CoaContractShips {
  coa_contract_id: number;
  id: number;
  ship_id: number;
}

interface CoaContracts {
  amount_unit_id: number | null;
  charterer_org_id: number;
  currency: string | null;
  end_date: string;
  id: number;
  inactive: boolean;
  max_amount: string | null;
  min_amount: string | null;
  name: string | null;
  shipowner_org_id: number;
  start_date: string;
  use_highest_freight_rate: boolean;
}

interface Commission {
  assortment_id: number | null;
  baf_applied: boolean | null;
  demurrage_applied: boolean | null;
  discharge_port_id: number | null;
  extra_freight_applied: boolean | null;
  id: number;
  load_port_id: number | null;
  organization_id: number | null;
  type: string;
  value: string;
  voyage_charter_id: number | null;
}

interface CommitLog {
  committed_at: string;
  committed_by: number | null;
  id: number;
  master_client_id: string | null;
  scenario_client_id: string | null;
}

interface CommitLogScenarios {
  client_id: string;
  id: number;
}

interface Files {
  id: number;
  note: string | null;
  type: string;
}

interface HandledByCoa {
  client_id: string;
  coa_id: number;
  discharging_concurrent: boolean;
  discharging_duration: string;
  discharging_duration_override: string | null;
  discharging_end: string;
  discharging_sea_duration_override: string | null;
  discharging_start: string;
  discharging_start_override: string | null;
  id: number;
  loading_concurrent: boolean;
  loading_duration: string;
  loading_duration_override: string | null;
  loading_end: string;
  loading_sea_duration_override: string | null;
  loading_start: string;
  loading_start_override: string | null;
  metadata: string | null;
  order_id: number;
  shared: boolean;
  updated_at: string | null;
  vessel_id: number | null;
}

interface HandledBySpot {
  client_id: string;
  discharging_concurrent: boolean;
  discharging_duration: string;
  discharging_duration_override: string | null;
  discharging_end: string;
  discharging_sea_duration_override: string | null;
  discharging_start: string;
  discharging_start_override: string | null;
  id: number;
  loading_concurrent: boolean;
  loading_duration: string;
  loading_duration_override: string | null;
  loading_end: string;
  loading_sea_duration_override: string | null;
  loading_start: string;
  loading_start_override: string | null;
  metadata: string | null;
  order_id: number | null;
  owner_organisation_id: number | null;
  shared: boolean;
  updated_at: string | null;
  vessel_id: number | null;
}

interface LatestPortCostByPortAndPrimaryAttribute {
  port_cost: string | null;
  port_cost_currency: string | null;
  port_name: string | null;
  port_type: string | null;
  primary_attribute_id: number | null;
}

interface OffHire {
  archived: boolean;
  ballast_bonus: string | null;
  ballast_bonus_commissionable: boolean | null;
  client_id: string;
  commission_pct: string | null;
  duration: string;
  end: string;
  id: number;
  location_a_id: number;
  location_b_id: number;
  locked: boolean;
  meta: string | null;
  planning_organisation_id: number;
  shared: boolean;
  start: string;
  tc_out_rate: string | null;
  type: string | null;
  updated_at: string | null;
  user_id: number;
  vessel_id: number | null;
}

interface OrderAttributeAssociations {
  id: number;
  order_attribute_id: number;
  order_id: number;
}

interface OrderDetails {
  cargo_deck_option: string | null;
  cargo_max_unit_weight: string | null;
  closing_date: string | null;
  consignee: string | null;
  external_remarks: string | null;
  extra_info: string | null;
  fixture_note: string | null;
  id: number;
  imo_class: string | null;
  inco_terms: string | null;
  order_id: number;
  order_name: string | null;
  receiver_organisation_id: number | null;
  sender_organisation_id: number | null;
  supply_contract_id: number | null;
}

interface OrderPortCallRelation {
  id: number;
  is_load_port: boolean;
  order_id: number;
  port_call_id: number;
}

interface OrderRequirementAssociation {
  id: number;
  order_id: number;
  special_requirement_id: number;
}

interface OrderVoyageCharterRelation {
  coa_route_id: number | null;
  id: number;
  order_id: number;
  outgoing: boolean;
  voyage_charter_id: number;
}

interface Orders {
  archived: boolean;
  assortment_id: number | null;
  cancelling: string | null;
  cargo_amount: string;
  cargo_unit_id: number | null;
  charterer_organization_id: number | null;
  coa_id: number | null;
  completed: string | null;
  created_at: string | null;
  delivered_before: string | null;
  discharge_port_id: number;
  discharging_overhead_duration: string | null;
  discharging_rate: string | null;
  discharging_unit: string | null;
  external_order_id: string | null;
  id: number;
  layday: string | null;
  load_port_id: number | null;
  loading_overhead_duration: string | null;
  loading_rate: string | null;
  loading_unit: string | null;
  margin_amount: string | null;
  margin_unit: string | null;
  min_vessel_class_requirement: number | null;
  nominated_vessel: string | null;
  rta: string | null;
  shipment_id: number | null;
  specific_gravity: string | null;
  started: string | null;
  stowage_factor_max: string | null;
  stowage_factor_min: string | null;
  to_be_deleted: boolean | null;
  user_id: number | null;
}

interface Organizations {
  broker: boolean;
  config_auto_accept_only_confirmed: boolean;
  config_cargo_allocation_combine_assortment: boolean;
  config_enable_accept_all_updates: boolean;
  config_enable_scenario_auto_save: boolean;
  config_events_concurrent_default: boolean;
  config_portcall_duration_berthapi_default: boolean;
  draft: string | null;
  id: number;
  name: string | null;
  port_cost_priority: string | null;
}

interface PortActivity {
  end: string | null;
  id: number;
  operation: string | null;
  start: string;
  statement_of_facts_id: number;
}

interface PortCall {
  archived: boolean;
  bunker_overrides: string | null;
  bunker_price_source: string | null;
  channel_cost_default_id: number | null;
  channel_duration_overhead: string | null;
  client_id: string;
  id: number;
  leg_avoids_channels: boolean;
  location_id: number;
  one_time_override_channel_cost: string | null;
  one_time_override_cost: string | null;
  planned_arrival: string | null;
  planned_arrival_duration: string | null;
  planned_arrival_override: string | null;
  planned_arrival_override_sourced_at: string | null;
  planned_arrival_override_sourced_from: string | null;
  planned_berthing: string | null;
  planned_berthing_duration: string | null;
  planned_berthing_override: string | null;
  planned_berthing_override_sourced_at: string | null;
  planned_berthing_override_sourced_from: string | null;
  planned_departure: string | null;
  planned_departure_duration: string | null;
  planned_departure_override: string | null;
  planned_departure_override_sourced_at: string | null;
  planned_departure_override_sourced_from: string | null;
  port_cost_default_id: number | null;
  previous_location_id: number | null;
  sea_duration_override: string | null;
  total_discharge_duration_override: string | null;
  total_load_duration_override: string | null;
  updated_at: string | null;
  use_historical_port_cost: boolean;
  use_integration_port_cost: boolean;
  use_latest_historical_port_cost: boolean;
  use_one_time_override_cost: boolean;
  vessel_id: number | null;
  weather_factor: string | null;
}

interface PortCallFiles {
  file_id: number;
  id: number;
  port_call_id: number;
}

interface PortCostDefault {
  cost: string;
  currency: string;
  id: number;
  location_id: number;
  vessel_id: number | null;
  vessel_type_id: number | null;
}

interface PortCostsByPortAndPrimaryAttribute {
  avg: string | null;
  count: number | null;
  max: string | null;
  median: number | null;
  min: string | null;
  port_cost_currency: string | null;
  port_name: string | null;
  port_type: string | null;
  primary_attribute_id: number | null;
}

interface PortCostsByPortAndVesselName {
  avg: string | null;
  count: number | null;
  max: string | null;
  median: number | null;
  min: string | null;
  port_cost_currency: string | null;
  port_name: string | null;
  port_type: string | null;
  vessel_name: string | null;
}

interface PortData {
  discharge_rate: string | null;
  id: number;
  laytime_days: string | null;
  loading_discharge_unit: string | null;
  loading_rate: string | null;
  port_id: number;
}

interface PortNotice {
  ata_berth: string | null;
  ata_pbp: string | null;
  berthing_prospects: string | null;
  eta: string | null;
  etd_berth: string | null;
  id: number;
  nor_tendered: string | null;
  port_call_id: number;
}

interface ScheduledOrder {
  cleaning_omitted: boolean;
  client_id: string;
  created_by_user_id: number | null;
  discharging_concurrent: boolean;
  discharging_duration: string;
  discharging_duration_override: string | null;
  discharging_end: string;
  discharging_sea_duration_override: string | null;
  discharging_start: string;
  discharging_start_override: string | null;
  id: number;
  loading_concurrent: boolean;
  loading_duration: string;
  loading_duration_override: string | null;
  loading_end: string;
  loading_sea_duration_override: string | null;
  loading_start: string;
  loading_start_override: string | null;
  order_id: number;
  shared: boolean;
  updated_at: string | null;
  vessel_id: number | null;
}

interface ShipVoyageNamingRuleRelation {
  id: number;
  rule_id: number;
  ship_id: number;
}

interface Shipment {
  client_id: string;
  external_shipment_id: string | null;
  id: number;
  name: string;
  remarks: string | null;
  voyage_attribute_id: number | null;
}

interface ShipmentExternalStatus {
  id: number;
  shipment_id: number;
  status: string;
}

interface ShipmentExtraFreight {
  client_id: string;
  created_at: string | null;
  description: string | null;
  freight_amount: string;
  freight_unit: string;
  id: number;
  name: string | null;
  shipment_id: number;
  updated_at: string | null;
}

interface ShipmentFreight {
  client_id: string;
  freight_amount: string;
  freight_unit: string;
  id: number;
  shipment_id: number;
}

interface Ships {
  breadth: string | null;
  crane_capacity: string | null;
  csi_score: string | null;
  deck_volume_capacity: string | null;
  default_bunker_grade_id: number | null;
  discharging_rate: string | null;
  discharging_unit: string | null;
  draft: string | null;
  enable_consecutive_coa_voyages: boolean;
  esi_score: string | null;
  external_fleet: boolean | null;
  gross_tonnage: string | null;
  id: number;
  imo: string | null;
  inactive: boolean;
  loa: string | null;
  loading_rate: string | null;
  loading_unit: string | null;
  main_engine_capacity: string | null;
  mass_capacity: string | null;
  mass_unit: string | null;
  name: string | null;
  net_tonnage: string | null;
  organization_id: number | null;
  primary_attribute_id: number | null;
  remarks: string | null;
  short_name: string | null;
  sort_id: string | null;
  tc_rate: string | null;
  vessel_info_id: number | null;
  volume_capacity: string | null;
  volume_unit: string | null;
  weight_capacity: number | null;
  weight_capacity_type: string | null;
}

interface SingleEvent {
  archived: boolean;
  cleaning_assortment_exclusion_id: number | null;
  client_id: string;
  concurrent: boolean;
  duration: string;
  end: string;
  id: number;
  location_id: number;
  locked: boolean;
  meta: string | null;
  sea_duration_override: string | null;
  shared: boolean;
  start: string;
  start_override: string | null;
  type: string;
  updated_at: string | null;
  vessel_id: number | null;
}

interface SingleEventPortCallRelation {
  id: number;
  port_call_id: number;
  single_event_id: number;
}

interface StatementOfFacts {
  arrival_time: string | null;
  berthed: string | null;
  id: number;
  loading_commenced: string | null;
  loading_completed: string | null;
  notice_of_readiness: string | null;
  pilot_onboard: string | null;
  port_call_id: number;
  sailing_time: string | null;
}

interface TcContract {
  delivery_end_date: string;
  delivery_port_id: number;
  delivery_start_date: string;
  external_id: string;
  id: number;
  redelivery_end_date: string;
  redelivery_port_id: number;
  redelivery_start_date: string;
  vessel_id: number;
}

interface Users {
  id: number;
  name: string | null;
}

interface VesselAttributes {
  cargo_compatibility_ranking: number | null;
  id: number;
  is_vessel_class: boolean;
  name: string;
}

interface VesselAttributesAssociations {
  id: number;
  ship_id: number;
  vessel_attribute_id: number;
}

interface VesselDraftDatapoints {
  cargo_load: string;
  draft: string;
  id: string;
  ship_id: number;
}

interface VesselFulfillsRequirementAssociation {
  id: number;
  special_requirement_id: number;
  vessel_attribute_id: number | null;
  vessel_id: number | null;
}

interface VesselInfo {
  discharging_rate: string | null;
  discharging_unit: string;
  id: number;
  loading_rate: string | null;
  loading_unit: string;
}

interface VesselSpeedConsumption {
  area: string | null;
  bunker_grade_id: number;
  consumption: string;
  id: number;
  ship_id: number;
  speed: string;
  tag: string | null;
  type: string;
}

interface VoyageCharter {
  cancelling: string | null;
  charterer_org_id: number | null;
  currency: string | null;
  delete_on_unassign: boolean;
  demurrage: string | null;
  external_id: string | null;
  id: number;
  is_inquiry: boolean;
  is_preliminary: boolean;
  is_template: boolean;
  layday: string | null;
  lumpsum: string | null;
  name: string | null;
  on_subs_until: string | null;
  payment_time: number | null;
  payment_trigger: string | null;
  payment_type: string | null;
  shipowner_org_id: number | null;
  vessel_id: number | null;
}

interface VoyageCharterExtraCost {
  amount: string;
  description: string | null;
  id: number;
  name: string | null;
  type: string | null;
  voyage_charter_id: number;
}

interface VoyageCharterExtraFreight {
  amount: string;
  description: string | null;
  id: number;
  ignore_in_pnl: boolean | null;
  name: string | null;
  type: string | null;
  voyage_charter_id: number;
}

interface VoyageCharterOrderFreight {
  freight_amount: string | null;
  freight_unit: string | null;
  id: number;
  order_voyage_charter_relation_id: number;
}

interface VoyageCharterPortData {
  id: number;
  port_data_id: number;
  voyage_charter_id: number;
}

interface VoyageCharterToContract {
  coa_id: number | null;
  id: number;
  voyage_charter_id: number;
}

interface VoyageNamingRule {
  id: number;
  rule: string;
  vessel_fullname: boolean | null;
  vessel_imo: boolean | null;
  vessel_shortname: boolean | null;
  year: string;
  zeros_pad: number;
}

interface ZoneLocation {
  id: number;
  location_id: number;
  zone_id: number;
}

interface Zones {
  id: number;
  name: string;
}

export interface BinaryPlanningPageQuery {
  api_orders_notices: ApiOrdersNotices[];
  assortment_groups: AssortmentGroups[];
  assortments: Assortments[];
  bunker_grade_override: BunkerGradeOverride[];
  cargo_compartment: CargoCompartment[];
  cargo_compartment_allocation: CargoCompartmentAllocation[];
  cargo_exclusion: CargoExclusion[];
  cargo_units: CargoUnits[];
  channel_cost_estimates: ChannelCostEstimates[];
  channel_incompatibilities: ChannelIncompatibilities[];
  coa_contract_assortments: CoaContractAssortments[];
  coa_contract_bunker: CoaContractBunker[];
  coa_contract_deviation_costs: CoaContractDeviationCosts[];
  coa_contract_port_data: CoaContractPortData[];
  coa_contract_ports: CoaContractPorts[];
  coa_contract_prices: CoaContractPrices[];
  coa_contract_ships: CoaContractShips[];
  coa_contracts: CoaContracts[];
  commission: Commission[];
  commit_log: CommitLog[];
  commit_log_scenarios: CommitLogScenarios[];
  files: Files[];
  handled_by_coa: HandledByCoa[];
  handled_by_spot: HandledBySpot[];
  latest_port_cost_by_port_and_primary_attribute: LatestPortCostByPortAndPrimaryAttribute[];
  off_hire: OffHire[];
  order_attribute_associations: OrderAttributeAssociations[];
  order_details: OrderDetails[];
  order_port_call_relation: OrderPortCallRelation[];
  order_requirement_association: OrderRequirementAssociation[];
  order_voyage_charter_relation: OrderVoyageCharterRelation[];
  orders: Orders[];
  organizations: Organizations[];
  port_activity: PortActivity[];
  port_call: PortCall[];
  port_call_files: PortCallFiles[];
  port_cost_default: PortCostDefault[];
  port_costs_by_port_and_primary_attribute: PortCostsByPortAndPrimaryAttribute[];
  port_costs_by_port_and_vessel_name: PortCostsByPortAndVesselName[];
  port_data: PortData[];
  port_notice: PortNotice[];
  scheduled_order: ScheduledOrder[];
  ship_voyage_naming_rule_relation: ShipVoyageNamingRuleRelation[];
  shipment: Shipment[];
  shipment_external_status: ShipmentExternalStatus[];
  shipment_extra_freight: ShipmentExtraFreight[];
  shipment_freight: ShipmentFreight[];
  ships: Ships[];
  single_event: SingleEvent[];
  single_event_port_call_relation: SingleEventPortCallRelation[];
  statement_of_facts: StatementOfFacts[];
  tc_contract: TcContract[];
  users: Users[];
  vessel_attributes: VesselAttributes[];
  vessel_attributes_associations: VesselAttributesAssociations[];
  vessel_draft_datapoints: VesselDraftDatapoints[];
  vessel_fulfills_requirement_association: VesselFulfillsRequirementAssociation[];
  vessel_info: VesselInfo[];
  vessel_speed_consumption: VesselSpeedConsumption[];
  voyage_charter: VoyageCharter[];
  voyage_charter_extra_cost: VoyageCharterExtraCost[];
  voyage_charter_extra_freight: VoyageCharterExtraFreight[];
  voyage_charter_order_freight: VoyageCharterOrderFreight[];
  voyage_charter_port_data: VoyageCharterPortData[];
  voyage_charter_to_contract: VoyageCharterToContract[];
  voyage_naming_rule: VoyageNamingRule[];
  zone_location: ZoneLocation[];
  zones: Zones[];
}

const oneOrNone = <T>(nullOrObject: T): T[] =>
  nullOrObject ? [nullOrObject] : [];

export const prepareInitialPlanningPageData = (
  json: BinaryPlanningPageQuery,
  locationsById: Map<number, ScenarioTimeline_OrderLocationFragment>,
): PlanningPageQuery => {
  /* Utility type to remove key optionality for all properties recursively */
  type RecursivelyDefinedProps<T> = T extends number | string
    ? T
    : T extends object
      ? {
          [K in keyof T]-?: RecursivelyDefinedProps<T[K]>;
        }
      : T;

  const allLocationsById = locationsById as Map<
    number,
    RecursivelyDefinedProps<ScenarioTimeline_OrderLocationFragment>
  >;
  const {
    api_orders_notices,
    assortment_groups,
    assortments,
    bunker_grade_override,
    cargo_compartment,
    cargo_compartment_allocation,
    cargo_exclusion,
    cargo_units,
    channel_cost_estimates,
    channel_incompatibilities,
    coa_contract_assortments,
    coa_contract_bunker,
    coa_contract_deviation_costs,
    coa_contract_port_data,
    coa_contract_ports,
    coa_contract_prices,
    coa_contract_ships,
    coa_contracts,
    commission,
    commit_log,
    commit_log_scenarios,
    files,
    handled_by_coa,
    handled_by_spot,
    latest_port_cost_by_port_and_primary_attribute,
    off_hire,
    order_attribute_associations,
    order_details,
    order_port_call_relation,
    order_requirement_association,
    order_voyage_charter_relation,
    orders,
    organizations,
    port_activity,
    port_call,
    port_call_files,
    port_cost_default,
    port_costs_by_port_and_primary_attribute,
    port_costs_by_port_and_vessel_name,
    port_data,
    port_notice,
    scheduled_order,
    ship_voyage_naming_rule_relation,
    shipment,
    shipment_external_status,
    shipment_extra_freight,
    shipment_freight,
    ships,
    single_event,
    single_event_port_call_relation,
    statement_of_facts,
    tc_contract,
    users,
    vessel_attributes,
    vessel_attributes_associations,
    vessel_draft_datapoints,
    vessel_fulfills_requirement_association,
    vessel_info,
    vessel_speed_consumption,
    voyage_charter,
    voyage_charter_extra_cost,
    voyage_charter_extra_freight,
    voyage_charter_order_freight,
    voyage_charter_port_data,
    voyage_charter_to_contract,
    voyage_naming_rule,
    zone_location,
    zones,
  } = json;

  const allApiOrdersNotices = api_orders_notices.map(
    ({ id, notices, order_id }) => {
      return {
        __typename: 'ApiOrdersNotice' as const,
        id: id as t.ApiOrdersNoticeId,
        notices: notices,
        orderId: order_id as t.OrderId,
      };
    },
  );
  allApiOrdersNotices.sort((a, b) => a.id - b.id);
  const allApiOrdersNoticesByOrderId = mapKeyBy(
    allApiOrdersNotices,
    (o) => o.orderId,
  );

  const allAssortmentGroups = assortment_groups.map(
    ({ external_id, id, name }) => {
      return {
        __typename: 'AssortmentGroup' as const,
        externalId: external_id,
        id: id as t.AssortmentGroupId,
        name: name,
      };
    },
  );
  allAssortmentGroups.sort((a, b) => a.id - b.id);
  const allAssortmentGroupsById = mapKeyBy(allAssortmentGroups, (o) => o.id);

  const allAssortments = assortments.map(
    ({
      assortment_group,
      cargo_type,
      discharging_rate,
      discharging_unit,
      fill_factor,
      id,
      loading_rate,
      loading_unit,
      margin_amount,
      margin_unit,
      name,
      specific_gravity,
      stowage_factor_max,
      stowage_factor_min,
    }) => {
      const allAssortmentGroupsById_assortment_group =
        assortment_group !== null
          ? (allAssortmentGroupsById.get(
              assortment_group as t.AssortmentGroupId,
            ) ?? null)
          : null;
      return {
        __typename: 'Assortment' as const,
        assortmentGroup: assortment_group as t.AssortmentGroupId,
        cargoType: mapCargoType(cargo_type),
        dischargingRate: discharging_rate,
        dischargingUnit: mapLoadingUnit(discharging_unit),
        fillFactor: fill_factor,
        id: id as t.AssortmentId,
        loadingRate: loading_rate,
        loadingUnit: mapLoadingUnit(loading_unit),
        marginAmount: margin_amount,
        marginUnit: mapMarginUnit(margin_unit),
        name: name,
        specificGravity: specific_gravity,
        stowageFactorMax: stowage_factor_max,
        stowageFactorMin: stowage_factor_min,
        assortmentGroupByAssortmentGroup:
          allAssortmentGroupsById_assortment_group,
        groupName: allAssortmentGroupsById_assortment_group
          ? `${name} ${allAssortmentGroupsById_assortment_group.name}`
          : null,
      };
    },
  );
  allAssortments.sort((a, b) => a.id - b.id);
  const allAssortmentsById = mapKeyBy(allAssortments, (o) => o.id);

  const allBunkerGradeOverrides = bunker_grade_override.map(
    ({
      carbon_factor,
      currency,
      grade_name,
      id,
      price,
      price_adjustment,
      reference_grade,
      reference_port,
    }) => {
      return {
        __typename: 'BunkerGradeOverride' as const,
        carbonFactor: carbon_factor,
        currency: mapCurrency(currency),
        gradeName: grade_name,
        id: id as t.BunkerGradeOverrideId,
        price: price,
        priceAdjustment: price_adjustment,
        referenceGrade: reference_grade,
        referencePort: reference_port,
      };
    },
  );
  allBunkerGradeOverrides.sort((a, b) => a.id - b.id);
  const allBunkerGradeOverridesById = mapKeyBy(
    allBunkerGradeOverrides,
    (o) => o.id,
  );

  const allCargoCompartments = cargo_compartment.map(
    ({ id, max_volume, name, ship_id, type, vessel_id, volume_unit }) => {
      return {
        __typename: 'CargoCompartment' as const,
        id: id as t.CargoCompartmentId,
        maxVolume: max_volume,
        name: name,
        shipId: ship_id as t.ShipId,
        type: mapHoldType(type),
        vesselId: vessel_id as t.VesselInfoId,
        volumeUnit: mapVolumeUnits(volume_unit),
      };
    },
  );
  allCargoCompartments.sort((a, b) => a.id - b.id);
  const allCargoCompartmentsByShipId = mapGroupBy(
    allCargoCompartments,
    (o) => o.shipId,
  );
  const allCargoCompartmentsByVesselId = mapGroupBy(
    allCargoCompartments,
    (o) => o.vesselId,
  );

  const allCargoCompartmentAllocations = cargo_compartment_allocation.map(
    ({
      amount,
      client_id,
      compartment_id,
      id,
      order_id,
      scheduled_order_client_id,
    }) => {
      return {
        __typename: 'CargoCompartmentAllocation' as const,
        amount: amount,
        clientId: client_id,
        compartmentId: compartment_id as t.CargoCompartmentId,
        id: id as t.CargoCompartmentAllocationId,
        orderId: order_id as t.OrderId,
        scheduledOrderClientId: scheduled_order_client_id,
      };
    },
  );
  allCargoCompartmentAllocations.sort((a, b) => a.id - b.id);
  const allCargoCompartmentAllocationsByOrderId = mapGroupBy(
    allCargoCompartmentAllocations,
    (o) => o.orderId,
  );

  const allCargoExclusions = cargo_exclusion.map(
    ({
      assortment,
      assortment_group,
      distance,
      id,
      order_id,
      tanks,
      voyages,
    }) => {
      const allAssortmentsById_assortment =
        assortment !== null
          ? (allAssortmentsById.get(assortment as t.AssortmentId) ?? null)
          : null;
      const allAssortmentGroupsById_assortment_group =
        assortment_group !== null
          ? (allAssortmentGroupsById.get(
              assortment_group as t.AssortmentGroupId,
            ) ?? null)
          : null;
      return {
        __typename: 'CargoExclusion' as const,
        assortment: assortment as t.AssortmentId,
        assortmentGroup: assortment_group as t.AssortmentGroupId,
        distance: distance,
        id: id as t.CargoExclusionId,
        orderId: order_id as t.OrderId,
        tanks: tanks,
        voyages: voyages,
        assortmentByAssortment: allAssortmentsById_assortment,
        assortmentGroupByAssortmentGroup:
          allAssortmentGroupsById_assortment_group,
      };
    },
  );
  allCargoExclusions.sort((a, b) => a.id - b.id);
  const allCargoExclusionsByOrderId = mapGroupBy(
    allCargoExclusions,
    (o) => o.orderId,
  );

  const allCargoUnits = cargo_units.map(({ id, name }) => {
    return {
      __typename: 'CargoUnit' as const,
      id: id as t.CargoUnitId,
      name: name,
    };
  });
  allCargoUnits.sort((a, b) => a.id - b.id);
  const allCargoUnitsById = mapKeyBy(allCargoUnits, (o) => o.id);

  const allChannelIncompatibilities = channel_incompatibilities.map(
    ({ channel_name, id, vessel_attribute_id }) => {
      return {
        __typename: 'ChannelIncompatibility' as const,
        channelName: channel_name,
        id: id as t.ChannelIncompatibilityId,
        vesselAttributeId: vessel_attribute_id as t.VesselAttributeId,
      };
    },
  );
  allChannelIncompatibilities.sort((a, b) => a.id - b.id);
  const allChannelIncompatibilitiesByVesselAttributeId = mapGroupBy(
    allChannelIncompatibilities,
    (o) => o.vesselAttributeId,
  );

  const allCoaContractAssortments = coa_contract_assortments.map(
    ({
      assortment_group_id,
      assortment_id,
      coa_contract_id,
      id,
      max_amount,
      min_amount,
      payment_unit,
    }) => {
      return {
        __typename: 'CoaContractAssortment' as const,
        assortmentGroupId: assortment_group_id as t.AssortmentGroupId,
        assortmentId: assortment_id as t.AssortmentId,
        coaContractId: coa_contract_id as t.CoaContractId,
        id: id as t.CoaContractAssortmentId,
        maxAmount: max_amount,
        minAmount: min_amount,
        paymentUnit: mapPaymentUnit(payment_unit),
      };
    },
  );
  allCoaContractAssortments.sort((a, b) => a.id - b.id);
  const allCoaContractAssortmentsByCoaContractId = mapGroupBy(
    allCoaContractAssortments,
    (o) => o.coaContractId,
  );

  const allCoaContractBunkers = coa_contract_bunker.map(
    ({
      baf_bunker_amount,
      baf_start_from_base,
      baf_step_effect,
      baf_step_size,
      baf_type,
      baf_use_step_effect_as_value,
      base_price_max,
      bunker_grade,
      coa_assortment_id,
      coa_contract_id,
      coa_route_id,
      id,
      price,
    }) => {
      return {
        __typename: 'CoaContractBunker' as const,
        bafBunkerAmount: baf_bunker_amount,
        bafStartFromBase: baf_start_from_base,
        bafStepEffect: baf_step_effect,
        bafStepSize: baf_step_size,
        bafType: mapBafTypes(baf_type),
        bafUseStepEffectAsValue: baf_use_step_effect_as_value,
        basePriceMax: base_price_max,
        bunkerGrade: mapBunkerGrade(bunker_grade),
        coaAssortmentId: coa_assortment_id as t.CoaContractAssortmentId,
        coaContractId: coa_contract_id as t.CoaContractId,
        coaRouteId: coa_route_id as t.CoaContractPortId,
        id: id as t.CoaContractBunkerId,
        price: price,
      };
    },
  );
  allCoaContractBunkers.sort((a, b) => a.id - b.id);
  const allCoaContractBunkersByCoaContractId = mapGroupBy(
    allCoaContractBunkers,
    (o) => o.coaContractId,
  );

  const allCoaContractDeviationCosts = coa_contract_deviation_costs.map(
    ({
      coa_contract_id,
      from_location_id,
      from_zone_id,
      id,
      lump_sum_amount,
      to_location_id,
      to_zone_id,
    }) => {
      return {
        __typename: 'CoaContractDeviationCost' as const,
        coaContractId: coa_contract_id as t.CoaContractId,
        fromLocationId: from_location_id as t.LocationId,
        fromZoneId: from_zone_id as t.ZoneId,
        id: id as t.CoaContractDeviationCostId,
        lumpSumAmount: lump_sum_amount,
        toLocationId: to_location_id as t.LocationId,
        toZoneId: to_zone_id as t.ZoneId,
      };
    },
  );
  allCoaContractDeviationCosts.sort((a, b) => a.id - b.id);
  const allCoaContractDeviationCostsByCoaContractId = mapGroupBy(
    allCoaContractDeviationCosts,
    (o) => o.coaContractId,
  );

  const allCoaContractShips = coa_contract_ships.map(
    ({ coa_contract_id, id, ship_id }) => {
      return {
        __typename: 'CoaContractShip' as const,
        coaContractId: coa_contract_id as t.CoaContractId,
        id: id as t.CoaContractShipId,
        shipId: ship_id as t.ShipId,
      };
    },
  );
  allCoaContractShips.sort((a, b) => a.id - b.id);
  const allCoaContractShipsByCoaContractId = mapGroupBy(
    allCoaContractShips,
    (o) => o.coaContractId,
  );

  const allCommitLogScenarios = commit_log_scenarios.map(
    ({ client_id, id }) => {
      return {
        __typename: 'CommitLogScenario' as const,
        clientId: client_id,
        id: id as t.CommitLogScenarioId,
      };
    },
  );
  allCommitLogScenarios.sort((a, b) => a.id - b.id);
  const allCommitLogScenariosByClientId = mapKeyBy(
    allCommitLogScenarios,
    (o) => o.clientId,
  );

  const allFiles = files.map(({ id, note, type }) => {
    return {
      __typename: 'File' as const,
      id: id as t.FileId,
      note: note,
      type: mapDocumentType(type),
    };
  });
  allFiles.sort((a, b) => a.id - b.id);
  const allFilesById = mapKeyBy(allFiles, (o) => o.id);

  const allHandledByCoas = handled_by_coa.map(
    ({
      client_id,
      coa_id,
      discharging_concurrent,
      discharging_duration,
      discharging_duration_override,
      discharging_end,
      discharging_sea_duration_override,
      discharging_start,
      discharging_start_override,
      id,
      loading_concurrent,
      loading_duration,
      loading_duration_override,
      loading_end,
      loading_sea_duration_override,
      loading_start,
      loading_start_override,
      metadata,
      order_id,
      shared,
      updated_at,
      vessel_id,
    }) => {
      return {
        __typename: 'HandledByCoa' as const,
        clientId: client_id,
        coaId: coa_id as t.CoaContractId,
        dischargingConcurrent: discharging_concurrent,
        dischargingDuration: discharging_duration,
        dischargingDurationOverride: discharging_duration_override,
        dischargingEnd: discharging_end,
        dischargingSeaDurationOverride: discharging_sea_duration_override,
        dischargingStart: discharging_start,
        dischargingStartOverride: discharging_start_override,
        id: id as t.HandledByCoaId,
        loadingConcurrent: loading_concurrent,
        loadingDuration: loading_duration,
        loadingDurationOverride: loading_duration_override,
        loadingEnd: loading_end,
        loadingSeaDurationOverride: loading_sea_duration_override,
        loadingStart: loading_start,
        loadingStartOverride: loading_start_override,
        metadata: metadata,
        orderId: order_id as t.OrderId,
        shared: shared,
        updatedAt: updated_at,
        vesselId: vessel_id as t.ShipId,
      };
    },
  );
  allHandledByCoas.sort((a, b) => a.id - b.id);

  const allHandledBySpots = handled_by_spot.map(
    ({
      client_id,
      discharging_concurrent,
      discharging_duration,
      discharging_duration_override,
      discharging_end,
      discharging_sea_duration_override,
      discharging_start,
      discharging_start_override,
      id,
      loading_concurrent,
      loading_duration,
      loading_duration_override,
      loading_end,
      loading_sea_duration_override,
      loading_start,
      loading_start_override,
      metadata,
      order_id,
      owner_organisation_id,
      shared,
      updated_at,
      vessel_id,
    }) => {
      return {
        __typename: 'HandledBySpot' as const,
        clientId: client_id,
        dischargingConcurrent: discharging_concurrent,
        dischargingDuration: discharging_duration,
        dischargingDurationOverride: discharging_duration_override,
        dischargingEnd: discharging_end,
        dischargingSeaDurationOverride: discharging_sea_duration_override,
        dischargingStart: discharging_start,
        dischargingStartOverride: discharging_start_override,
        id: id as t.HandledBySpotId,
        loadingConcurrent: loading_concurrent,
        loadingDuration: loading_duration,
        loadingDurationOverride: loading_duration_override,
        loadingEnd: loading_end,
        loadingSeaDurationOverride: loading_sea_duration_override,
        loadingStart: loading_start,
        loadingStartOverride: loading_start_override,
        metadata: metadata,
        orderId: order_id as t.OrderId,
        ownerOrganisationId: owner_organisation_id as t.OrganizationId,
        shared: shared,
        updatedAt: updated_at,
        vesselId: vessel_id as t.ShipId,
      };
    },
  );
  allHandledBySpots.sort((a, b) => a.id - b.id);
  const allHandledBySpotsByOrderId = mapKeyBy(
    allHandledBySpots,
    (o) => o.orderId,
  );

  const allLatestPortCostByPortAndPrimaryAttributes =
    latest_port_cost_by_port_and_primary_attribute.map(
      ({
        port_cost,
        port_cost_currency,
        port_name,
        port_type,
        primary_attribute_id,
      }) => {
        return {
          __typename: 'LatestPortCostByPortAndPrimaryAttribute' as const,
          portCost: port_cost,
          portCostCurrency: port_cost_currency,
          portName: port_name,
          portType: port_type,
          primaryAttributeId: primary_attribute_id,
        };
      },
    );

  const allOffHires = off_hire.map(
    ({
      archived,
      ballast_bonus,
      ballast_bonus_commissionable,
      client_id,
      commission_pct,
      duration,
      end,
      id,
      location_a_id,
      location_b_id,
      locked,
      meta,
      planning_organisation_id,
      shared,
      start,
      tc_out_rate,
      type,
      updated_at,
      user_id,
      vessel_id,
    }) => {
      const allLocationsById_location_a_id =
        allLocationsById.get(location_a_id as t.LocationId) ?? null;
      const allLocationsById_location_b_id =
        allLocationsById.get(location_b_id as t.LocationId) ?? null;
      return {
        __typename: 'OffHire' as const,
        archived: archived,
        ballastBonus: ballast_bonus,
        ballastBonusCommissionable: ballast_bonus_commissionable,
        clientId: client_id,
        commissionPct: commission_pct,
        duration: duration,
        end: end,
        id: id as t.OffHireId,
        locationAId: location_a_id as t.LocationId,
        locationBId: location_b_id as t.LocationId,
        locked: locked,
        meta: meta,
        planningOrganisationId: planning_organisation_id as t.OrganizationId,
        shared: shared,
        start: start,
        tcOutRate: tc_out_rate,
        type: mapOffHireType(type),
        updatedAt: updated_at,
        userId: user_id as t.UserId,
        vesselId: vessel_id as t.ShipId,
        locationByLocationAId: allLocationsById_location_a_id,
        locationByLocationBId: allLocationsById_location_b_id,
      };
    },
  );
  allOffHires.sort((a, b) => a.id - b.id);

  const allOrderAttributeAssociations = order_attribute_associations.map(
    ({ id, order_attribute_id, order_id }) => {
      return {
        __typename: 'OrderAttributeAssociation' as const,
        id: id as t.OrderAttributeAssociationId,
        orderAttributeId: order_attribute_id as t.OrderAttributeId,
        orderId: order_id as t.OrderId,
      };
    },
  );
  allOrderAttributeAssociations.sort((a, b) => a.id - b.id);
  const allOrderAttributeAssociationsByOrderId = mapGroupBy(
    allOrderAttributeAssociations,
    (o) => o.orderId,
  );

  const allOrderDetails = order_details.map(
    ({
      cargo_deck_option,
      cargo_max_unit_weight,
      closing_date,
      consignee,
      external_remarks,
      extra_info,
      fixture_note,
      id,
      imo_class,
      inco_terms,
      order_id,
      order_name,
      receiver_organisation_id,
      sender_organisation_id,
      supply_contract_id,
    }) => {
      return {
        __typename: 'OrderDetail' as const,
        cargoDeckOption: mapCargoDeckOptionType(cargo_deck_option),
        cargoMaxUnitWeight: cargo_max_unit_weight,
        closingDate: closing_date,
        consignee: consignee,
        externalRemarks: external_remarks,
        extraInfo: extra_info,
        fixtureNote: fixture_note,
        id: id as t.OrderDetailId,
        imoClass: mapImoClass(imo_class),
        incoTerms: mapIncoTermsType(inco_terms),
        orderId: order_id as t.OrderId,
        orderName: order_name,
        receiverOrganisationId: receiver_organisation_id as t.OrganizationId,
        senderOrganisationId: sender_organisation_id as t.OrganizationId,
        supplyContractId: supply_contract_id as t.SupplyContractId,
      };
    },
  );
  allOrderDetails.sort((a, b) => a.id - b.id);
  const allOrderDetailsByOrderId = mapGroupBy(
    allOrderDetails,
    (o) => o.orderId,
  );

  const allOrderRequirementAssociations = order_requirement_association.map(
    ({ id, order_id, special_requirement_id }) => {
      return {
        __typename: 'OrderRequirementAssociation' as const,
        id: id as t.OrderRequirementAssociationId,
        orderId: order_id as t.OrderId,
        specialRequirementId: special_requirement_id as t.SpecialRequirementId,
      };
    },
  );
  allOrderRequirementAssociations.sort((a, b) => a.id - b.id);
  const allOrderRequirementAssociationsByOrderId = mapGroupBy(
    allOrderRequirementAssociations,
    (o) => o.orderId,
  );

  const allOrganizations = organizations.map(
    ({
      broker,
      config_auto_accept_only_confirmed,
      config_cargo_allocation_combine_assortment,
      config_enable_accept_all_updates,
      config_enable_scenario_auto_save,
      config_events_concurrent_default,
      config_portcall_duration_berthapi_default,
      draft,
      id,
      name,
      port_cost_priority,
    }) => {
      return {
        __typename: 'Organization' as const,
        broker: broker,
        configAutoAcceptOnlyConfirmed: config_auto_accept_only_confirmed,
        configCargoAllocationCombineAssortment:
          config_cargo_allocation_combine_assortment,
        configEnableAcceptAllUpdates: config_enable_accept_all_updates,
        configEnableScenarioAutoSave: config_enable_scenario_auto_save,
        configEventsConcurrentDefault: config_events_concurrent_default,
        configPortcallDurationBerthapiDefault:
          config_portcall_duration_berthapi_default,
        draft: draft,
        id: id as t.OrganizationId,
        name: name,
        portCostPriority: port_cost_priority,
      };
    },
  );
  allOrganizations.sort((a, b) => a.id - b.id);
  const allOrganizationsById = mapKeyBy(allOrganizations, (o) => o.id);

  const allCommissions = commission.map(
    ({
      assortment_id,
      baf_applied,
      demurrage_applied,
      discharge_port_id,
      extra_freight_applied,
      id,
      load_port_id,
      organization_id,
      type,
      value,
      voyage_charter_id,
    }) => {
      const allOrganizationsById_organization_id =
        organization_id !== null
          ? (allOrganizationsById.get(organization_id as t.OrganizationId) ??
            null)
          : null;
      return {
        __typename: 'Commission' as const,
        assortmentId: assortment_id as t.AssortmentId,
        bafApplied: baf_applied,
        demurrageApplied: demurrage_applied,
        dischargePortId: discharge_port_id as t.LocationId,
        extraFreightApplied: extra_freight_applied,
        id: id as t.CommissionId,
        loadPortId: load_port_id as t.LocationId,
        organizationId: organization_id as t.OrganizationId,
        type: mapCommissionType(type),
        value: value,
        voyageCharterId: voyage_charter_id as t.VoyageCharterId,
        organizationByOrganizationId: allOrganizationsById_organization_id,
      };
    },
  );
  allCommissions.sort((a, b) => a.id - b.id);
  const allCommissionsByVoyageCharterId = mapGroupBy(
    allCommissions,
    (o) => o.voyageCharterId,
  );

  const allPortActivities = port_activity.map(
    ({ end, id, operation, start, statement_of_facts_id }) => {
      return {
        __typename: 'PortActivity' as const,
        end: end,
        id: id as t.PortActivityId,
        operation: operation,
        start: start,
        statementOfFactsId: statement_of_facts_id as t.StatementOfFactId,
      };
    },
  );
  allPortActivities.sort((a, b) => a.id - b.id);
  const allPortActivitiesByStatementOfFactsId = mapGroupBy(
    allPortActivities,
    (o) => o.statementOfFactsId,
  );

  const allPortCallFiles = port_call_files.map(
    ({ file_id, id, port_call_id }) => {
      const allFilesById_file_id =
        allFilesById.get(file_id as t.FileId) ?? null;
      return {
        __typename: 'PortCallFile' as const,
        fileId: file_id as t.FileId,
        id: id as t.PortCallFileId,
        portCallId: port_call_id as t.PortCallId,
        fileByFileId: allFilesById_file_id,
      };
    },
  );
  allPortCallFiles.sort((a, b) => a.id - b.id);
  const allPortCallFilesByPortCallId = mapGroupBy(
    allPortCallFiles,
    (o) => o.portCallId,
  );

  const allPortCostsByPortAndPrimaryAttributes =
    port_costs_by_port_and_primary_attribute.map(
      ({
        avg,
        count,
        max,
        median,
        min,
        port_cost_currency,
        port_name,
        port_type,
        primary_attribute_id,
      }) => {
        return {
          __typename: 'PortCostsByPortAndPrimaryAttribute' as const,
          avg: avg,
          count: count,
          max: max,
          median: median,
          min: min,
          portCostCurrency: port_cost_currency,
          portName: port_name,
          portType: port_type,
          primaryAttributeId: primary_attribute_id,
        };
      },
    );

  const allPortCostsByPortAndVesselNames =
    port_costs_by_port_and_vessel_name.map(
      ({
        avg,
        count,
        max,
        median,
        min,
        port_cost_currency,
        port_name,
        port_type,
        vessel_name,
      }) => {
        return {
          __typename: 'PortCostsByPortAndVesselName' as const,
          avg: avg,
          count: count,
          max: max,
          median: median,
          min: min,
          portCostCurrency: port_cost_currency,
          portName: port_name,
          portType: port_type,
          vesselName: vessel_name,
        };
      },
    );

  const allPortData = port_data.map(
    ({
      discharge_rate,
      id,
      laytime_days,
      loading_discharge_unit,
      loading_rate,
      port_id,
    }) => {
      return {
        __typename: 'PortDatum' as const,
        dischargeRate: discharge_rate,
        id: id as t.PortDatumId,
        laytimeDays: mapShincShex(laytime_days),
        loadingDischargeUnit: mapLoadingUnit(loading_discharge_unit),
        loadingRate: loading_rate,
        portId: port_id as t.LocationId,
      };
    },
  );
  allPortData.sort((a, b) => a.id - b.id);
  const allPortDataById = mapKeyBy(allPortData, (o) => o.id);

  const allCoaContractPortData = coa_contract_port_data.map(
    ({ coa_contract_id, id, port_data_id }) => {
      const allPortDataById_port_data_id =
        allPortDataById.get(port_data_id as t.PortDatumId) ?? null;
      return {
        __typename: 'CoaContractPortDatum' as const,
        coaContractId: coa_contract_id as t.CoaContractId,
        id: id as t.CoaContractPortDatumId,
        portDataId: port_data_id as t.PortDatumId,
        portData: allPortDataById_port_data_id,
      };
    },
  );
  allCoaContractPortData.sort((a, b) => a.id - b.id);
  const allCoaContractPortDataByCoaContractId = mapGroupBy(
    allCoaContractPortData,
    (o) => o.coaContractId,
  );

  const allPortNotices = port_notice.map(
    ({
      ata_berth,
      ata_pbp,
      berthing_prospects,
      eta,
      etd_berth,
      id,
      nor_tendered,
      port_call_id,
    }) => {
      return {
        __typename: 'PortNotice' as const,
        ataBerth: ata_berth,
        ataPbp: ata_pbp,
        berthingProspects: berthing_prospects,
        eta: eta,
        etdBerth: etd_berth,
        id: id as t.PortNoticeId,
        norTendered: nor_tendered,
        portCallId: port_call_id as t.PortCallId,
      };
    },
  );
  allPortNotices.sort((a, b) => a.id - b.id);
  const allPortNoticesByPortCallId = mapKeyBy(
    allPortNotices,
    (o) => o.portCallId,
  );

  const allShipmentExternalStatuses = shipment_external_status.map(
    ({ id, shipment_id, status }) => {
      return {
        __typename: 'ShipmentExternalStatus' as const,
        id: id as t.ShipmentExternalStatusId,
        shipmentId: shipment_id as t.ShipmentId,
        status: mapShipmentStatus(status),
      };
    },
  );
  allShipmentExternalStatuses.sort((a, b) => a.id - b.id);
  const allShipmentExternalStatusesByShipmentId = mapKeyBy(
    allShipmentExternalStatuses,
    (o) => o.shipmentId,
  );

  const allShipmentExtraFreights = shipment_extra_freight.map(
    ({
      client_id,
      created_at,
      description,
      freight_amount,
      freight_unit,
      id,
      name,
      shipment_id,
      updated_at,
    }) => {
      return {
        __typename: 'ShipmentExtraFreight' as const,
        clientId: client_id,
        createdAt: created_at,
        description: description,
        freightAmount: freight_amount,
        freightUnit: mapPaymentUnit(freight_unit),
        id: id as t.ShipmentExtraFreightId,
        name: name,
        shipmentId: shipment_id as t.ShipmentId,
        updatedAt: updated_at,
      };
    },
  );
  allShipmentExtraFreights.sort((a, b) => a.id - b.id);
  const allShipmentExtraFreightsByShipmentId = mapGroupBy(
    allShipmentExtraFreights,
    (o) => o.shipmentId,
  );

  const allShipmentFreights = shipment_freight.map(
    ({ client_id, freight_amount, freight_unit, id, shipment_id }) => {
      return {
        __typename: 'ShipmentFreight' as const,
        clientId: client_id,
        freightAmount: freight_amount,
        freightUnit: mapPaymentUnit(freight_unit),
        id: id as t.ShipmentFreightId,
        shipmentId: shipment_id as t.ShipmentId,
      };
    },
  );
  allShipmentFreights.sort((a, b) => a.id - b.id);
  const allShipmentFreightsByShipmentId = mapKeyBy(
    allShipmentFreights,
    (o) => o.shipmentId,
  );

  const allShipments = shipment.map(
    ({
      client_id,
      external_shipment_id,
      id,
      name,
      remarks,
      voyage_attribute_id,
    }) => {
      const allShipmentExternalStatusesByShipmentId_id =
        allShipmentExternalStatusesByShipmentId.get(id as t.ShipmentId) ?? null;
      const allShipmentFreightsByShipmentId_id =
        allShipmentFreightsByShipmentId.get(id as t.ShipmentId) ?? null;
      const allShipmentExtraFreightsByShipmentId_id =
        allShipmentExtraFreightsByShipmentId.get(id as t.ShipmentId) ?? [];
      return {
        __typename: 'Shipment' as const,
        clientId: client_id,
        externalShipmentId: external_shipment_id,
        id: id as t.ShipmentId,
        name: name,
        remarks: remarks,
        voyageAttributeId: voyage_attribute_id as t.VoyageAttributeId,
        shipmentExternalStatusesByShipmentId: {
          __typename: 'ShipmentExternalStatusesConnection' as const,
          nodes: oneOrNone(allShipmentExternalStatusesByShipmentId_id),
        },
        shipmentFreightByShipmentId: allShipmentFreightsByShipmentId_id,
        shipmentExtraFreightsByShipmentId: {
          __typename: 'ShipmentExtraFreightsConnection' as const,
          nodes: allShipmentExtraFreightsByShipmentId_id,
        },
      };
    },
  );
  allShipments.sort((a, b) => a.id - b.id);

  const allSingleEventPortCallRelations = single_event_port_call_relation.map(
    ({ id, port_call_id, single_event_id }) => {
      return {
        __typename: 'SingleEventPortCallRelation' as const,
        id: id as t.SingleEventPortCallRelationId,
        portCallId: port_call_id as t.PortCallId,
        singleEventId: single_event_id as t.SingleEventId,
      };
    },
  );
  allSingleEventPortCallRelations.sort((a, b) => a.id - b.id);
  const allSingleEventPortCallRelationsBySingleEventId = mapKeyBy(
    allSingleEventPortCallRelations,
    (o) => o.singleEventId,
  );

  const allSingleEvents = single_event.map(
    ({
      archived,
      cleaning_assortment_exclusion_id,
      client_id,
      concurrent,
      duration,
      end,
      id,
      location_id,
      locked,
      meta,
      sea_duration_override,
      shared,
      start,
      start_override,
      type,
      updated_at,
      vessel_id,
    }) => {
      const allLocationsById_location_id =
        allLocationsById.get(location_id as t.LocationId) ?? null;
      const allSingleEventPortCallRelationsBySingleEventId_id =
        allSingleEventPortCallRelationsBySingleEventId.get(
          id as t.SingleEventId,
        ) ?? null;
      return {
        __typename: 'SingleEvent' as const,
        archived: archived,
        cleaningAssortmentExclusionId:
          cleaning_assortment_exclusion_id as t.AssortmentExclusionId,
        clientId: client_id,
        concurrent: concurrent,
        duration: duration,
        end: end,
        id: id as t.SingleEventId,
        locationId: location_id as t.LocationId,
        locked: locked,
        meta: meta,
        seaDurationOverride: sea_duration_override,
        shared: shared,
        start: start,
        startOverride: start_override,
        type: mapEventType(type),
        updatedAt: updated_at,
        vesselId: vessel_id as t.ShipId,
        locationByLocationId: allLocationsById_location_id,
        singleEventPortCallRelationsBySingleEventId: {
          __typename: 'SingleEventPortCallRelationsConnection' as const,
          nodes: oneOrNone(allSingleEventPortCallRelationsBySingleEventId_id),
        },
      };
    },
  );
  allSingleEvents.sort((a, b) => a.id - b.id);

  const allStatementOfFacts = statement_of_facts.map(
    ({
      arrival_time,
      berthed,
      id,
      loading_commenced,
      loading_completed,
      notice_of_readiness,
      pilot_onboard,
      port_call_id,
      sailing_time,
    }) => {
      const allPortActivitiesByStatementOfFactsId_id =
        allPortActivitiesByStatementOfFactsId.get(id as t.StatementOfFactId) ??
        [];
      return {
        __typename: 'StatementOfFact' as const,
        arrivalTime: arrival_time,
        berthed: berthed,
        id: id as t.StatementOfFactId,
        loadingCommenced: loading_commenced,
        loadingCompleted: loading_completed,
        noticeOfReadiness: notice_of_readiness,
        pilotOnboard: pilot_onboard,
        portCallId: port_call_id as t.PortCallId,
        sailingTime: sailing_time,
        portActivitiesByStatementOfFactsId: {
          __typename: 'PortActivitiesConnection' as const,
          nodes: allPortActivitiesByStatementOfFactsId_id,
        },
      };
    },
  );
  allStatementOfFacts.sort((a, b) => a.id - b.id);
  const allStatementOfFactsByPortCallId = mapGroupBy(
    allStatementOfFacts,
    (o) => o.portCallId,
  );

  const allPortCalls = port_call.map(
    ({
      archived,
      bunker_overrides,
      bunker_price_source,
      channel_cost_default_id,
      channel_duration_overhead,
      client_id,
      id,
      leg_avoids_channels,
      location_id,
      one_time_override_channel_cost,
      one_time_override_cost,
      planned_arrival,
      planned_arrival_duration,
      planned_arrival_override,
      planned_arrival_override_sourced_at,
      planned_arrival_override_sourced_from,
      planned_berthing,
      planned_berthing_duration,
      planned_berthing_override,
      planned_berthing_override_sourced_at,
      planned_berthing_override_sourced_from,
      planned_departure,
      planned_departure_duration,
      planned_departure_override,
      planned_departure_override_sourced_at,
      planned_departure_override_sourced_from,
      port_cost_default_id,
      previous_location_id,
      sea_duration_override,
      total_discharge_duration_override,
      total_load_duration_override,
      updated_at,
      use_historical_port_cost,
      use_integration_port_cost,
      use_latest_historical_port_cost,
      use_one_time_override_cost,
      vessel_id,
      weather_factor,
    }) => {
      const allLocationsById_location_id =
        allLocationsById.get(location_id as t.LocationId) ?? null;
      const allPortNoticesByPortCallId_id =
        allPortNoticesByPortCallId.get(id as t.PortCallId) ?? null;
      const allStatementOfFactsByPortCallId_id =
        allStatementOfFactsByPortCallId.get(id as t.PortCallId) ?? [];
      const allPortCallFilesByPortCallId_id =
        allPortCallFilesByPortCallId.get(id as t.PortCallId) ?? [];
      return {
        __typename: 'PortCall' as const,
        archived: archived,
        bunkerOverrides: bunker_overrides,
        bunkerPriceSource: bunker_price_source,
        channelCostDefaultId:
          channel_cost_default_id as t.ChannelCostEstimateId,
        channelDurationOverhead: channel_duration_overhead,
        clientId: client_id,
        id: id as t.PortCallId,
        legAvoidsChannels: leg_avoids_channels,
        locationId: location_id as t.LocationId,
        oneTimeOverrideChannelCost: one_time_override_channel_cost,
        oneTimeOverrideCost: one_time_override_cost,
        plannedArrival: planned_arrival,
        plannedArrivalDuration: planned_arrival_duration,
        plannedArrivalOverride: planned_arrival_override,
        plannedArrivalOverrideSourcedAt: planned_arrival_override_sourced_at,
        plannedArrivalOverrideSourcedFrom:
          planned_arrival_override_sourced_from,
        plannedBerthing: planned_berthing,
        plannedBerthingDuration: planned_berthing_duration,
        plannedBerthingOverride: planned_berthing_override,
        plannedBerthingOverrideSourcedAt: planned_berthing_override_sourced_at,
        plannedBerthingOverrideSourcedFrom:
          planned_berthing_override_sourced_from,
        plannedDeparture: planned_departure,
        plannedDepartureDuration: planned_departure_duration,
        plannedDepartureOverride: planned_departure_override,
        plannedDepartureOverrideSourcedAt:
          planned_departure_override_sourced_at,
        plannedDepartureOverrideSourcedFrom:
          planned_departure_override_sourced_from,
        portCostDefaultId: port_cost_default_id as t.PortCostDefaultId,
        previousLocationId: previous_location_id as t.LocationId,
        seaDurationOverride: sea_duration_override,
        totalDischargeDurationOverride: total_discharge_duration_override,
        totalLoadDurationOverride: total_load_duration_override,
        updatedAt: updated_at,
        useHistoricalPortCost: use_historical_port_cost,
        useIntegrationPortCost: use_integration_port_cost,
        useLatestHistoricalPortCost: use_latest_historical_port_cost,
        useOneTimeOverrideCost: use_one_time_override_cost,
        vesselId: vessel_id as t.ShipId,
        weatherFactor: weather_factor,
        locationByLocationId: allLocationsById_location_id,
        portNoticesByPortCallId: {
          __typename: 'PortNoticesConnection' as const,
          nodes: oneOrNone(allPortNoticesByPortCallId_id),
        },
        statementOfFactsByPortCallId: {
          __typename: 'StatementOfFactsConnection' as const,
          nodes: allStatementOfFactsByPortCallId_id,
        },
        portCallFilesByPortCallId: {
          __typename: 'PortCallFilesConnection' as const,
          nodes: allPortCallFilesByPortCallId_id,
        },
      };
    },
  );
  allPortCalls.sort((a, b) => a.id - b.id);
  const allPortCallsById = mapKeyBy(allPortCalls, (o) => o.id);

  const allOrderPortCallRelations = order_port_call_relation.map(
    ({ id, is_load_port, order_id, port_call_id }) => {
      const allPortCallsById_port_call_id =
        allPortCallsById.get(port_call_id as t.PortCallId) ?? null;
      return {
        __typename: 'OrderPortCallRelation' as const,
        id: id as t.OrderPortCallRelationId,
        isLoadPort: is_load_port,
        orderId: order_id as t.OrderId,
        portCallId: port_call_id as t.PortCallId,
        portCallByPortCallId: allPortCallsById_port_call_id,
      };
    },
  );
  allOrderPortCallRelations.sort((a, b) => a.id - b.id);
  const allOrderPortCallRelationsByOrderId = mapGroupBy(
    allOrderPortCallRelations,
    (o) => o.orderId,
  );

  const allTcContracts = tc_contract.map(
    ({
      delivery_end_date,
      delivery_port_id,
      delivery_start_date,
      external_id,
      id,
      redelivery_end_date,
      redelivery_port_id,
      redelivery_start_date,
      vessel_id,
    }) => {
      return {
        __typename: 'TcContract' as const,
        deliveryEndDate: delivery_end_date,
        deliveryPortId: delivery_port_id as t.LocationId,
        deliveryStartDate: delivery_start_date,
        externalId: external_id,
        id: id as t.TcContractId,
        redeliveryEndDate: redelivery_end_date,
        redeliveryPortId: redelivery_port_id as t.LocationId,
        redeliveryStartDate: redelivery_start_date,
        vesselId: vessel_id as t.ShipId,
      };
    },
  );
  allTcContracts.sort((a, b) => a.id - b.id);
  const allTcContractsByVesselId = mapGroupBy(
    allTcContracts,
    (o) => o.vesselId,
  );

  const allUsers = users.map(({ id, name }) => {
    return {
      __typename: 'User' as const,
      id: id as t.UserId,
      name: name,
    };
  });
  allUsers.sort((a, b) => a.id - b.id);
  const allUsersById = mapKeyBy(allUsers, (o) => o.id);

  const allScheduledOrders = scheduled_order.map(
    ({
      cleaning_omitted,
      client_id,
      created_by_user_id,
      discharging_concurrent,
      discharging_duration,
      discharging_duration_override,
      discharging_end,
      discharging_sea_duration_override,
      discharging_start,
      discharging_start_override,
      id,
      loading_concurrent,
      loading_duration,
      loading_duration_override,
      loading_end,
      loading_sea_duration_override,
      loading_start,
      loading_start_override,
      order_id,
      shared,
      updated_at,
      vessel_id,
    }) => {
      const allUsersById_created_by_user_id =
        created_by_user_id !== null
          ? (allUsersById.get(created_by_user_id as t.UserId) ?? null)
          : null;
      return {
        __typename: 'ScheduledOrder' as const,
        cleaningOmitted: cleaning_omitted,
        clientId: client_id,
        createdByUserId: created_by_user_id as t.UserId,
        dischargingConcurrent: discharging_concurrent,
        dischargingDuration: discharging_duration,
        dischargingDurationOverride: discharging_duration_override,
        dischargingEnd: discharging_end,
        dischargingSeaDurationOverride: discharging_sea_duration_override,
        dischargingStart: discharging_start,
        dischargingStartOverride: discharging_start_override,
        id: id as t.ScheduledOrderId,
        loadingConcurrent: loading_concurrent,
        loadingDuration: loading_duration,
        loadingDurationOverride: loading_duration_override,
        loadingEnd: loading_end,
        loadingSeaDurationOverride: loading_sea_duration_override,
        loadingStart: loading_start,
        loadingStartOverride: loading_start_override,
        orderId: order_id as t.OrderId,
        shared: shared,
        updatedAt: updated_at,
        vesselId: vessel_id as t.ShipId,
        userByCreatedByUserId: allUsersById_created_by_user_id,
      };
    },
  );
  allScheduledOrders.sort((a, b) => a.id - b.id);
  const allScheduledOrdersByOrderId = mapGroupBy(
    allScheduledOrders,
    (o) => o.orderId,
  );

  const allCommitLogs = commit_log.map(
    ({
      committed_at,
      committed_by,
      id,
      master_client_id,
      scenario_client_id,
    }) => {
      const allUsersById_committed_by =
        committed_by !== null
          ? (allUsersById.get(committed_by as t.UserId) ?? null)
          : null;
      const allCommitLogScenariosByClientId_master_client_id =
        master_client_id !== null
          ? (allCommitLogScenariosByClientId.get(master_client_id) ?? null)
          : null;
      return {
        __typename: 'CommitLog' as const,
        committedAt: committed_at,
        committedBy: committed_by as t.UserId,
        id: id as t.CommitLogId,
        masterClientId: master_client_id,
        scenarioClientId: scenario_client_id,
        userByCommittedBy: allUsersById_committed_by,
        commitLogScenarioByMasterClientId:
          allCommitLogScenariosByClientId_master_client_id,
      };
    },
  );
  allCommitLogs.sort((a, b) => a.id - b.id);

  const allVesselDraftDatapoints = vessel_draft_datapoints.map(
    ({ cargo_load, draft, id, ship_id }) => {
      return {
        __typename: 'VesselDraftDatapoint' as const,
        cargoLoad: cargo_load,
        draft: draft,
        id: id as t.VesselDraftDatapointIdString,
        shipId: ship_id as t.ShipId,
      };
    },
  );
  allVesselDraftDatapoints.sort((a, b) => {
    const aId = a.id.toString();
    const bId = b.id.toString();
    return aId.length - bId.length || aId.localeCompare(bId);
  });
  const allVesselDraftDatapointsByShipId = mapGroupBy(
    allVesselDraftDatapoints,
    (o) => o.shipId,
  );

  const allVesselFulfillsRequirementAssociations =
    vessel_fulfills_requirement_association.map(
      ({ id, special_requirement_id, vessel_attribute_id, vessel_id }) => {
        return {
          __typename: 'VesselFulfillsRequirementAssociation' as const,
          id: id as t.VesselFulfillsRequirementAssociationId,
          specialRequirementId:
            special_requirement_id as t.SpecialRequirementId,
          vesselAttributeId: vessel_attribute_id as t.VesselAttributeId,
          vesselId: vessel_id as t.ShipId,
        };
      },
    );
  allVesselFulfillsRequirementAssociations.sort((a, b) => a.id - b.id);
  const allVesselFulfillsRequirementAssociationsByVesselAttributeId =
    mapGroupBy(
      allVesselFulfillsRequirementAssociations,
      (o) => o.vesselAttributeId,
    );
  const allVesselFulfillsRequirementAssociationsByVesselId = mapGroupBy(
    allVesselFulfillsRequirementAssociations,
    (o) => o.vesselId,
  );

  const allVesselAttributes = vessel_attributes.map(
    ({ cargo_compatibility_ranking, id, is_vessel_class, name }) => {
      const allVesselFulfillsRequirementAssociationsByVesselAttributeId_id =
        allVesselFulfillsRequirementAssociationsByVesselAttributeId.get(
          id as t.VesselAttributeId,
        ) ?? [];
      const allChannelIncompatibilitiesByVesselAttributeId_id =
        allChannelIncompatibilitiesByVesselAttributeId.get(
          id as t.VesselAttributeId,
        ) ?? [];
      return {
        __typename: 'VesselAttribute' as const,
        cargoCompatibilityRanking: cargo_compatibility_ranking,
        id: id as t.VesselAttributeId,
        isVesselClass: is_vessel_class,
        name: name,
        vesselFulfillsRequirementAssociationsByVesselAttributeId: {
          __typename:
            'VesselFulfillsRequirementAssociationsConnection' as const,
          nodes: allVesselFulfillsRequirementAssociationsByVesselAttributeId_id,
        },
        channelIncompatibilitiesByVesselAttributeId: {
          __typename: 'ChannelIncompatibilitiesConnection' as const,
          nodes: allChannelIncompatibilitiesByVesselAttributeId_id,
        },
      };
    },
  );
  allVesselAttributes.sort((a, b) => a.id - b.id);
  const allVesselAttributesById = mapKeyBy(allVesselAttributes, (o) => o.id);

  const allVesselAttributesAssociations = vessel_attributes_associations.map(
    ({ id, ship_id, vessel_attribute_id }) => {
      const allVesselAttributesById_vessel_attribute_id =
        allVesselAttributesById.get(
          vessel_attribute_id as t.VesselAttributeId,
        ) ?? null;
      return {
        __typename: 'VesselAttributesAssociation' as const,
        id: id as t.VesselAttributesAssociationId,
        shipId: ship_id as t.ShipId,
        vesselAttributeId: vessel_attribute_id as t.VesselAttributeId,
        vesselAttributeByVesselAttributeId:
          allVesselAttributesById_vessel_attribute_id,
      };
    },
  );
  allVesselAttributesAssociations.sort((a, b) => a.id - b.id);
  const allVesselAttributesAssociationsByShipId = mapGroupBy(
    allVesselAttributesAssociations,
    (o) => o.shipId,
  );

  const allPortCostDefaults = port_cost_default.map(
    ({ cost, currency, id, location_id, vessel_id, vessel_type_id }) => {
      const allVesselAttributesById_vessel_type_id =
        vessel_type_id !== null
          ? (allVesselAttributesById.get(
              vessel_type_id as t.VesselAttributeId,
            ) ?? null)
          : null;
      return {
        __typename: 'PortCostDefault' as const,
        cost: cost,
        currency: mapCurrency(currency),
        id: id as t.PortCostDefaultId,
        locationId: location_id as t.LocationId,
        vesselId: vessel_id as t.ShipId,
        vesselTypeId: vessel_type_id as t.VesselAttributeId,
        vesselAttributeByVesselTypeId: allVesselAttributesById_vessel_type_id,
      };
    },
  );
  allPortCostDefaults.sort((a, b) => a.id - b.id);

  const allVesselInfos = vessel_info.map(
    ({
      discharging_rate,
      discharging_unit,
      id,
      loading_rate,
      loading_unit,
    }) => {
      const allCargoCompartmentsByVesselId_id =
        allCargoCompartmentsByVesselId.get(id as t.VesselInfoId) ?? [];
      return {
        __typename: 'VesselInfo' as const,
        dischargingRate: discharging_rate,
        dischargingUnit: mapLoadingUnit(discharging_unit),
        id: id as t.VesselInfoId,
        loadingRate: loading_rate,
        loadingUnit: mapLoadingUnit(loading_unit),
        cargoCompartmentsByVesselId: {
          __typename: 'CargoCompartmentsConnection' as const,
          nodes: allCargoCompartmentsByVesselId_id,
        },
      };
    },
  );
  allVesselInfos.sort((a, b) => a.id - b.id);
  const allVesselInfosById = mapKeyBy(allVesselInfos, (o) => o.id);

  const allVesselSpeedConsumptions = vessel_speed_consumption.map(
    ({ area, bunker_grade_id, consumption, id, ship_id, speed, tag, type }) => {
      const allBunkerGradeOverridesById_bunker_grade_id =
        allBunkerGradeOverridesById.get(
          bunker_grade_id as t.BunkerGradeOverrideId,
        ) ?? null;
      return {
        __typename: 'VesselSpeedConsumption' as const,
        area: mapVesselArea(area),
        bunkerGradeId: bunker_grade_id as t.BunkerGradeOverrideId,
        consumption: consumption,
        id: id as t.VesselSpeedConsumptionId,
        shipId: ship_id as t.ShipId,
        speed: speed,
        tag: mapVesselSpeedTag(tag),
        type: mapVesselSpeedType(type),
        bunkerGradeOverrideByBunkerGradeId:
          allBunkerGradeOverridesById_bunker_grade_id,
      };
    },
  );
  allVesselSpeedConsumptions.sort((a, b) => a.id - b.id);
  const allVesselSpeedConsumptionsByShipId = mapGroupBy(
    allVesselSpeedConsumptions,
    (o) => o.shipId,
  );

  const allVoyageCharterExtraCosts = voyage_charter_extra_cost.map(
    ({ amount, description, id, name, type, voyage_charter_id }) => {
      return {
        __typename: 'VoyageCharterExtraCost' as const,
        amount: amount,
        description: description,
        id: id as t.VoyageCharterExtraCostId,
        name: name,
        type: mapVoyageCharterCostType(type),
        voyageCharterId: voyage_charter_id as t.VoyageCharterId,
      };
    },
  );
  allVoyageCharterExtraCosts.sort((a, b) => a.id - b.id);
  const allVoyageCharterExtraCostsByVoyageCharterId = mapGroupBy(
    allVoyageCharterExtraCosts,
    (o) => o.voyageCharterId,
  );

  const allVoyageCharterExtraFreights = voyage_charter_extra_freight.map(
    ({
      amount,
      description,
      id,
      ignore_in_pnl,
      name,
      type,
      voyage_charter_id,
    }) => {
      return {
        __typename: 'VoyageCharterExtraFreight' as const,
        amount: amount,
        description: description,
        id: id as t.VoyageCharterExtraFreightId,
        ignoreInPnl: ignore_in_pnl,
        name: name,
        type: mapFreightType(type),
        voyageCharterId: voyage_charter_id as t.VoyageCharterId,
      };
    },
  );
  allVoyageCharterExtraFreights.sort((a, b) => a.id - b.id);
  const allVoyageCharterExtraFreightsByVoyageCharterId = mapGroupBy(
    allVoyageCharterExtraFreights,
    (o) => o.voyageCharterId,
  );

  const allVoyageCharterOrderFreights = voyage_charter_order_freight.map(
    ({
      freight_amount,
      freight_unit,
      id,
      order_voyage_charter_relation_id,
    }) => {
      return {
        __typename: 'VoyageCharterOrderFreight' as const,
        freightAmount: freight_amount,
        freightUnit: mapPaymentUnit(freight_unit),
        id: id as t.VoyageCharterOrderFreightId,
        orderVoyageCharterRelationId:
          order_voyage_charter_relation_id as t.OrderVoyageCharterRelationId,
      };
    },
  );
  allVoyageCharterOrderFreights.sort((a, b) => a.id - b.id);
  const allVoyageCharterOrderFreightsByOrderVoyageCharterRelationId =
    mapGroupBy(
      allVoyageCharterOrderFreights,
      (o) => o.orderVoyageCharterRelationId,
    );

  const allVoyageCharterPortData = voyage_charter_port_data.map(
    ({ id, port_data_id, voyage_charter_id }) => {
      const allPortDataById_port_data_id =
        allPortDataById.get(port_data_id as t.PortDatumId) ?? null;
      return {
        __typename: 'VoyageCharterPortDatum' as const,
        id: id as t.VoyageCharterPortDatumId,
        portDataId: port_data_id as t.PortDatumId,
        voyageCharterId: voyage_charter_id as t.VoyageCharterId,
        portDatumByPortDataId: allPortDataById_port_data_id,
      };
    },
  );
  allVoyageCharterPortData.sort((a, b) => a.id - b.id);
  const allVoyageCharterPortDataByVoyageCharterId = mapGroupBy(
    allVoyageCharterPortData,
    (o) => o.voyageCharterId,
  );

  const allVoyageNamingRules = voyage_naming_rule.map(
    ({
      id,
      rule,
      vessel_fullname,
      vessel_imo,
      vessel_shortname,
      year,
      zeros_pad,
    }) => {
      return {
        __typename: 'VoyageNamingRule' as const,
        id: id as t.VoyageNamingRuleId,
        rule: rule,
        vesselFullname: vessel_fullname,
        vesselImo: vessel_imo,
        vesselShortname: vessel_shortname,
        year: mapVoyageNamingYearFormat(year),
        zerosPad: zeros_pad,
      };
    },
  );
  allVoyageNamingRules.sort((a, b) => a.id - b.id);
  const allVoyageNamingRulesById = mapKeyBy(allVoyageNamingRules, (o) => o.id);

  const allShipVoyageNamingRuleRelations = ship_voyage_naming_rule_relation.map(
    ({ id, rule_id, ship_id }) => {
      const allVoyageNamingRulesById_rule_id =
        allVoyageNamingRulesById.get(rule_id as t.VoyageNamingRuleId) ?? null;
      return {
        __typename: 'ShipVoyageNamingRuleRelation' as const,
        id: id as t.ShipVoyageNamingRuleRelationId,
        ruleId: rule_id as t.VoyageNamingRuleId,
        shipId: ship_id as t.ShipId,
        voyageNamingRuleByRuleId: allVoyageNamingRulesById_rule_id,
      };
    },
  );
  allShipVoyageNamingRuleRelations.sort((a, b) => a.id - b.id);
  const allShipVoyageNamingRuleRelationsByShipId = mapKeyBy(
    allShipVoyageNamingRuleRelations,
    (o) => o.shipId,
  );

  const allShips = ships.map(
    ({
      breadth,
      crane_capacity,
      csi_score,
      deck_volume_capacity,
      default_bunker_grade_id,
      discharging_rate,
      discharging_unit,
      draft,
      enable_consecutive_coa_voyages,
      esi_score,
      external_fleet,
      gross_tonnage,
      id,
      imo,
      inactive,
      loa,
      loading_rate,
      loading_unit,
      main_engine_capacity,
      mass_capacity,
      mass_unit,
      name,
      net_tonnage,
      organization_id,
      primary_attribute_id,
      remarks,
      short_name,
      sort_id,
      tc_rate,
      vessel_info_id,
      volume_capacity,
      volume_unit,
      weight_capacity,
      weight_capacity_type,
    }) => {
      const allVesselAttributesById_primary_attribute_id =
        primary_attribute_id !== null
          ? (allVesselAttributesById.get(
              primary_attribute_id as t.VesselAttributeId,
            ) ?? null)
          : null;
      const allCargoCompartmentsByShipId_id =
        allCargoCompartmentsByShipId.get(id as t.ShipId) ?? [];
      const allVesselFulfillsRequirementAssociationsByVesselId_id =
        allVesselFulfillsRequirementAssociationsByVesselId.get(
          id as t.ShipId,
        ) ?? [];
      const allVesselInfosById_vessel_info_id =
        vessel_info_id !== null
          ? (allVesselInfosById.get(vessel_info_id as t.VesselInfoId) ?? null)
          : null;
      const allVesselAttributesAssociationsByShipId_id =
        allVesselAttributesAssociationsByShipId.get(id as t.ShipId) ?? [];
      const allVesselSpeedConsumptionsByShipId_id =
        allVesselSpeedConsumptionsByShipId.get(id as t.ShipId) ?? [];
      const allVesselDraftDatapointsByShipId_id =
        allVesselDraftDatapointsByShipId.get(id as t.ShipId) ?? [];
      const allShipVoyageNamingRuleRelationsByShipId_id =
        allShipVoyageNamingRuleRelationsByShipId.get(id as t.ShipId) ?? null;
      const allTcContractsByVesselId_id =
        allTcContractsByVesselId.get(id as t.ShipId) ?? [];
      return {
        __typename: 'Ship' as const,
        breadth: breadth,
        craneCapacity: crane_capacity,
        csiScore: csi_score,
        deckVolumeCapacity: deck_volume_capacity,
        defaultBunkerGradeId:
          default_bunker_grade_id as t.BunkerGradeOverrideId,
        dischargingRate: discharging_rate,
        dischargingUnit: mapLoadingUnit(discharging_unit),
        draft: draft,
        enableConsecutiveCoaVoyages: enable_consecutive_coa_voyages,
        esiScore: esi_score,
        externalFleet: external_fleet,
        grossTonnage: gross_tonnage,
        id: id as t.ShipId,
        imo: imo,
        inactive: inactive,
        loa: loa,
        loadingRate: loading_rate,
        loadingUnit: mapLoadingUnit(loading_unit),
        mainEngineCapacity: main_engine_capacity,
        massCapacity: mass_capacity,
        massUnit: mapWeightUnits(mass_unit),
        name: name,
        netTonnage: net_tonnage,
        organizationId: organization_id as t.OrganizationId,
        primaryAttributeId: primary_attribute_id as t.VesselAttributeId,
        remarks: remarks,
        shortName: short_name,
        sortId: sort_id,
        tcRate: tc_rate,
        vesselInfoId: vessel_info_id as t.VesselInfoId,
        volumeCapacity: volume_capacity,
        volumeUnit: mapVolumeUnits(volume_unit),
        weightCapacity: weight_capacity,
        weightCapacityType: mapWeightCapacityType(weight_capacity_type),
        vesselAttributeByPrimaryAttributeId:
          allVesselAttributesById_primary_attribute_id,
        cargoCompartmentsByShipId: {
          __typename: 'CargoCompartmentsConnection' as const,
          nodes: allCargoCompartmentsByShipId_id,
        },
        vesselFulfillsRequirementAssociationsByVesselId: {
          __typename:
            'VesselFulfillsRequirementAssociationsConnection' as const,
          nodes: allVesselFulfillsRequirementAssociationsByVesselId_id,
        },
        vesselInfoByVesselInfoId: allVesselInfosById_vessel_info_id,
        vesselAttributesAssociationsByShipId: {
          __typename: 'VesselAttributesAssociationsConnection' as const,
          nodes: allVesselAttributesAssociationsByShipId_id,
        },
        vesselSpeedConsumptionsByShipId: {
          __typename: 'VesselSpeedConsumptionsConnection' as const,
          nodes: allVesselSpeedConsumptionsByShipId_id,
        },
        vesselDraftDatapointsByShipId: {
          __typename: 'VesselDraftDatapointsConnection' as const,
          nodes: allVesselDraftDatapointsByShipId_id,
        },
        shipVoyageNamingRuleRelationsByShipId: {
          __typename: 'ShipVoyageNamingRuleRelationsConnection' as const,
          nodes: oneOrNone(allShipVoyageNamingRuleRelationsByShipId_id),
        },
        tcContractsByVesselId: {
          __typename: 'TcContractsConnection' as const,
          nodes: allTcContractsByVesselId_id,
        },
      };
    },
  );
  allShips.sort((a, b) => a.id - b.id);
  const allShipsById = mapKeyBy(allShips, (o) => o.id);

  const allChannelCostEstimates = channel_cost_estimates.map(
    ({
      assortment_group_id,
      cargo_condition,
      channel_cost,
      channel_cost_currency,
      channel_name,
      duration_overhead,
      id,
      vessel_id,
      vessel_type_id,
    }) => {
      const allVesselAttributesById_vessel_type_id =
        vessel_type_id !== null
          ? (allVesselAttributesById.get(
              vessel_type_id as t.VesselAttributeId,
            ) ?? null)
          : null;
      const allShipsById_vessel_id =
        vessel_id !== null
          ? (allShipsById.get(vessel_id as t.ShipId) ?? null)
          : null;
      const allAssortmentGroupsById_assortment_group_id =
        assortment_group_id !== null
          ? (allAssortmentGroupsById.get(
              assortment_group_id as t.AssortmentGroupId,
            ) ?? null)
          : null;
      return {
        __typename: 'ChannelCostEstimate' as const,
        assortmentGroupId: assortment_group_id as t.AssortmentGroupId,
        cargoCondition: mapChannelCostCargoCondition(cargo_condition),
        channelCost: channel_cost,
        channelCostCurrency: channel_cost_currency,
        channelName: channel_name,
        durationOverhead: duration_overhead,
        id: id as t.ChannelCostEstimateId,
        vesselId: vessel_id as t.ShipId,
        vesselTypeId: vessel_type_id as t.VesselAttributeId,
        vesselAttributeByVesselTypeId: allVesselAttributesById_vessel_type_id,
        shipByVesselId: allShipsById_vessel_id,
        assortmentGroupByAssortmentGroupId:
          allAssortmentGroupsById_assortment_group_id,
      };
    },
  );
  allChannelCostEstimates.sort((a, b) => a.id - b.id);

  const allZoneLocations = zone_location.map(({ id, location_id, zone_id }) => {
    return {
      __typename: 'ZoneLocation' as const,
      id: id as t.ZoneLocationId,
      locationId: location_id as t.LocationId,
      zoneId: zone_id as t.ZoneId,
    };
  });
  allZoneLocations.sort((a, b) => a.id - b.id);
  const allZoneLocationsByLocationId = mapGroupBy(
    allZoneLocations,
    (o) => o.locationId,
  );
  const allZoneLocationsByZoneId = mapGroupBy(
    allZoneLocations,
    (o) => o.zoneId,
  );

  const allZones = zones.map(({ id, name }) => {
    const allZoneLocationsByZoneId_id =
      allZoneLocationsByZoneId.get(id as t.ZoneId) ?? [];
    return {
      __typename: 'Zone' as const,
      id: id as t.ZoneId,
      name: name,
      zoneLocationsByZoneId: {
        __typename: 'ZoneLocationsConnection' as const,
        nodes: allZoneLocationsByZoneId_id,
      },
    };
  });
  allZones.sort((a, b) => a.id - b.id);
  const allZonesById = mapKeyBy(allZones, (o) => o.id);

  const allCoaContractPorts = coa_contract_ports.map(
    ({
      coa_contract_id,
      destination_id,
      destination_zone_id,
      id,
      origin_id,
      origin_zone_id,
    }) => {
      const allZonesById_destination_zone_id =
        destination_zone_id !== null
          ? (allZonesById.get(destination_zone_id as t.ZoneId) ?? null)
          : null;
      const allZonesById_origin_zone_id =
        origin_zone_id !== null
          ? (allZonesById.get(origin_zone_id as t.ZoneId) ?? null)
          : null;
      return {
        __typename: 'CoaContractPort' as const,
        coaContractId: coa_contract_id as t.CoaContractId,
        destinationId: destination_id as t.LocationId,
        destinationZoneId: destination_zone_id as t.ZoneId,
        id: id as t.CoaContractPortId,
        originId: origin_id as t.LocationId,
        originZoneId: origin_zone_id as t.ZoneId,
        zoneByDestinationZoneId: allZonesById_destination_zone_id,
        zoneByOriginZoneId: allZonesById_origin_zone_id,
      };
    },
  );
  allCoaContractPorts.sort((a, b) => a.id - b.id);
  const allCoaContractPortsByCoaContractId = mapGroupBy(
    allCoaContractPorts,
    (o) => o.coaContractId,
  );
  const allCoaContractPortsById = mapKeyBy(allCoaContractPorts, (o) => o.id);

  const allCoaContractPrices = coa_contract_prices.map(
    ({ coa_assortment_id, coa_contract_id, id, price, route_id }) => {
      const allCoaContractPortsById_route_id =
        route_id !== null
          ? (allCoaContractPortsById.get(route_id as t.CoaContractPortId) ??
            null)
          : null;
      return {
        __typename: 'CoaContractPrice' as const,
        coaAssortmentId: coa_assortment_id as t.CoaContractAssortmentId,
        coaContractId: coa_contract_id as t.CoaContractId,
        id: id as t.CoaContractPriceId,
        price: price,
        routeId: route_id as t.CoaContractPortId,
        coaContractPortByRouteId: allCoaContractPortsById_route_id,
      };
    },
  );
  allCoaContractPrices.sort((a, b) => a.id - b.id);
  const allCoaContractPricesByCoaContractId = mapGroupBy(
    allCoaContractPrices,
    (o) => o.coaContractId,
  );

  const allCoaContracts = coa_contracts.map(
    ({
      amount_unit_id,
      charterer_org_id,
      currency,
      end_date,
      id,
      inactive,
      max_amount,
      min_amount,
      name,
      shipowner_org_id,
      start_date,
      use_highest_freight_rate,
    }) => {
      const allCargoUnitsById_amount_unit_id =
        amount_unit_id !== null
          ? (allCargoUnitsById.get(amount_unit_id as t.CargoUnitId) ?? null)
          : null;
      const allCoaContractBunkersByCoaContractId_id =
        allCoaContractBunkersByCoaContractId.get(id as t.CoaContractId) ?? [];
      const allCoaContractAssortmentsByCoaContractId_id =
        allCoaContractAssortmentsByCoaContractId.get(id as t.CoaContractId) ??
        [];
      const allCoaContractPortsByCoaContractId_id =
        allCoaContractPortsByCoaContractId.get(id as t.CoaContractId) ?? [];
      const allCoaContractPricesByCoaContractId_id =
        allCoaContractPricesByCoaContractId.get(id as t.CoaContractId) ?? [];
      const allCoaContractDeviationCostsByCoaContractId_id =
        allCoaContractDeviationCostsByCoaContractId.get(
          id as t.CoaContractId,
        ) ?? [];
      const allOrganizationsById_shipowner_org_id =
        allOrganizationsById.get(shipowner_org_id as t.OrganizationId) ?? null;
      const allCoaContractShipsByCoaContractId_id =
        allCoaContractShipsByCoaContractId.get(id as t.CoaContractId) ?? [];
      const allCoaContractPortDataByCoaContractId_id =
        allCoaContractPortDataByCoaContractId.get(id as t.CoaContractId) ?? [];
      return {
        __typename: 'CoaContract' as const,
        amountUnitId: amount_unit_id as t.CargoUnitId,
        chartererOrgId: charterer_org_id as t.OrganizationId,
        currency: mapCurrency(currency),
        endDate: end_date,
        id: id as t.CoaContractId,
        inactive: inactive,
        maxAmount: max_amount,
        minAmount: min_amount,
        name: name,
        shipownerOrgId: shipowner_org_id as t.OrganizationId,
        startDate: start_date,
        useHighestFreightRate: use_highest_freight_rate,
        cargoUnitByAmountUnitId: allCargoUnitsById_amount_unit_id,
        coaContractBunkersByCoaContractId: {
          __typename: 'CoaContractBunkersConnection' as const,
          nodes: allCoaContractBunkersByCoaContractId_id,
        },
        coaContractAssortmentsByCoaContractId: {
          __typename: 'CoaContractAssortmentsConnection' as const,
          nodes: allCoaContractAssortmentsByCoaContractId_id,
        },
        coaContractPortsByCoaContractId: {
          __typename: 'CoaContractPortsConnection' as const,
          nodes: allCoaContractPortsByCoaContractId_id,
        },
        coaContractPricesByCoaContractId: {
          __typename: 'CoaContractPricesConnection' as const,
          nodes: allCoaContractPricesByCoaContractId_id,
        },
        coaContractDeviationCostsByCoaContractId: {
          __typename: 'CoaContractDeviationCostsConnection' as const,
          nodes: allCoaContractDeviationCostsByCoaContractId_id,
        },
        shipowner: allOrganizationsById_shipowner_org_id,
        vessels: {
          __typename: 'CoaContractShipsConnection' as const,
          nodes: allCoaContractShipsByCoaContractId_id,
        },
        portDataRelations: {
          __typename: 'CoaContractPortDataConnection' as const,
          nodes: allCoaContractPortDataByCoaContractId_id,
        },
        coaAssortments: {
          __typename: 'CoaContractAssortmentsConnection' as const,
          nodes: allCoaContractAssortmentsByCoaContractId_id,
        },
        ports: {
          __typename: 'CoaContractPortsConnection' as const,
          nodes: allCoaContractPortsByCoaContractId_id,
        },
      };
    },
  );
  allCoaContracts.sort((a, b) => a.id - b.id);
  const allCoaContractsById = mapKeyBy(allCoaContracts, (o) => o.id);

  const allVoyageCharterToContracts = voyage_charter_to_contract.map(
    ({ coa_id, id, voyage_charter_id }) => {
      const allCoaContractsById_coa_id =
        coa_id !== null
          ? (allCoaContractsById.get(coa_id as t.CoaContractId) ?? null)
          : null;
      return {
        __typename: 'VoyageCharterToContract' as const,
        coaId: coa_id as t.CoaContractId,
        id: id as t.VoyageCharterToContractId,
        voyageCharterId: voyage_charter_id as t.VoyageCharterId,
        coaContractByCoaId: allCoaContractsById_coa_id,
      };
    },
  );
  allVoyageCharterToContracts.sort((a, b) => a.id - b.id);
  const allVoyageCharterToContractsByVoyageCharterId = mapKeyBy(
    allVoyageCharterToContracts,
    (o) => o.voyageCharterId,
  );

  const allVoyageCharters = voyage_charter.map(
    ({
      cancelling,
      charterer_org_id,
      currency,
      delete_on_unassign,
      demurrage,
      external_id,
      id,
      is_inquiry,
      is_preliminary,
      is_template,
      layday,
      lumpsum,
      name,
      on_subs_until,
      payment_time,
      payment_trigger,
      payment_type,
      shipowner_org_id,
      vessel_id,
    }) => {
      const allOrganizationsById_charterer_org_id =
        charterer_org_id !== null
          ? (allOrganizationsById.get(charterer_org_id as t.OrganizationId) ??
            null)
          : null;
      const allVoyageCharterToContractsByVoyageCharterId_id =
        allVoyageCharterToContractsByVoyageCharterId.get(
          id as t.VoyageCharterId,
        ) ?? null;
      const allCommissionsByVoyageCharterId_id =
        allCommissionsByVoyageCharterId.get(id as t.VoyageCharterId) ?? [];
      const allVoyageCharterExtraFreightsByVoyageCharterId_id =
        allVoyageCharterExtraFreightsByVoyageCharterId.get(
          id as t.VoyageCharterId,
        ) ?? [];
      const allVoyageCharterExtraCostsByVoyageCharterId_id =
        allVoyageCharterExtraCostsByVoyageCharterId.get(
          id as t.VoyageCharterId,
        ) ?? [];
      const allVoyageCharterPortDataByVoyageCharterId_id =
        allVoyageCharterPortDataByVoyageCharterId.get(
          id as t.VoyageCharterId,
        ) ?? [];
      return {
        __typename: 'VoyageCharter' as const,
        cancelling: cancelling,
        chartererOrgId: charterer_org_id as t.OrganizationId,
        currency: mapCurrency(currency),
        deleteOnUnassign: delete_on_unassign,
        demurrage: demurrage,
        externalId: external_id,
        id: id as t.VoyageCharterId,
        isInquiry: is_inquiry,
        isPreliminary: is_preliminary,
        isTemplate: is_template,
        layday: layday,
        lumpsum: lumpsum,
        name: name,
        onSubsUntil: on_subs_until,
        paymentTime: payment_time,
        paymentTrigger: mapPaymentTrigger(payment_trigger),
        paymentType: mapPaymentType(payment_type),
        shipownerOrgId: shipowner_org_id as t.OrganizationId,
        vesselId: vessel_id as t.ShipId,
        organizationByChartererOrgId: allOrganizationsById_charterer_org_id,
        voyageCharterToContractsByVoyageCharterId: {
          __typename: 'VoyageCharterToContractsConnection' as const,
          nodes: oneOrNone(allVoyageCharterToContractsByVoyageCharterId_id),
        },
        commissionsByVoyageCharterId: {
          __typename: 'CommissionsConnection' as const,
          nodes: allCommissionsByVoyageCharterId_id,
        },
        voyageCharterExtraFreightsByVoyageCharterId: {
          __typename: 'VoyageCharterExtraFreightsConnection' as const,
          nodes: allVoyageCharterExtraFreightsByVoyageCharterId_id,
        },
        voyageCharterExtraCostsByVoyageCharterId: {
          __typename: 'VoyageCharterExtraCostsConnection' as const,
          nodes: allVoyageCharterExtraCostsByVoyageCharterId_id,
        },
        voyageCharterPortDataByVoyageCharterId: {
          __typename: 'VoyageCharterPortDataConnection' as const,
          nodes: allVoyageCharterPortDataByVoyageCharterId_id,
        },
      };
    },
  );
  allVoyageCharters.sort((a, b) => a.id - b.id);
  const allVoyageChartersById = mapKeyBy(allVoyageCharters, (o) => o.id);

  const allOrderVoyageCharterRelations = order_voyage_charter_relation.map(
    ({ coa_route_id, id, order_id, outgoing, voyage_charter_id }) => {
      const allVoyageCharterOrderFreightsByOrderVoyageCharterRelationId_id =
        allVoyageCharterOrderFreightsByOrderVoyageCharterRelationId.get(
          id as t.OrderVoyageCharterRelationId,
        ) ?? [];
      const allVoyageChartersById_voyage_charter_id =
        allVoyageChartersById.get(voyage_charter_id as t.VoyageCharterId) ??
        null;
      return {
        __typename: 'OrderVoyageCharterRelation' as const,
        coaRouteId: coa_route_id as t.CoaContractPortId,
        id: id as t.OrderVoyageCharterRelationId,
        orderId: order_id as t.OrderId,
        outgoing: outgoing,
        voyageCharterId: voyage_charter_id as t.VoyageCharterId,
        voyageCharterOrderFreightsByOrderVoyageCharterRelationId: {
          __typename: 'VoyageCharterOrderFreightsConnection' as const,
          nodes: allVoyageCharterOrderFreightsByOrderVoyageCharterRelationId_id,
        },
        voyageCharterByVoyageCharterId: allVoyageChartersById_voyage_charter_id,
      };
    },
  );
  allOrderVoyageCharterRelations.sort((a, b) => a.id - b.id);
  const ovcrByOrderIdAndOutgoing = mapKeyBy2(
    allOrderVoyageCharterRelations,
    (ovcr) => ovcr.orderId,
    (ovcr) => ovcr.outgoing,
  );

  const allOrders = orders.map(
    ({
      archived,
      assortment_id,
      cancelling,
      cargo_amount,
      cargo_unit_id,
      charterer_organization_id,
      coa_id,
      completed,
      created_at,
      delivered_before,
      discharge_port_id,
      discharging_overhead_duration,
      discharging_rate,
      discharging_unit,
      external_order_id,
      id,
      layday,
      load_port_id,
      loading_overhead_duration,
      loading_rate,
      loading_unit,
      margin_amount,
      margin_unit,
      min_vessel_class_requirement,
      nominated_vessel,
      rta,
      shipment_id,
      specific_gravity,
      started,
      stowage_factor_max,
      stowage_factor_min,
      to_be_deleted,
      user_id,
    }) => {
      const ovcrs = ovcrByOrderIdAndOutgoing.get(id as t.OrderId);
      const allAssortmentsById_assortment_id =
        assortment_id !== null
          ? (allAssortmentsById.get(assortment_id as t.AssortmentId) ?? null)
          : null;
      const allLocationsById_discharge_port_id =
        allLocationsById.get(discharge_port_id as t.LocationId) ?? null;
      const allLocationsById_load_port_id =
        load_port_id !== null
          ? (allLocationsById.get(load_port_id as t.LocationId) ?? null)
          : null;
      const allScheduledOrdersByOrderId_id =
        allScheduledOrdersByOrderId.get(id as t.OrderId) ?? [];
      const allOrderRequirementAssociationsByOrderId_id =
        allOrderRequirementAssociationsByOrderId.get(id as t.OrderId) ?? [];
      const allCargoUnitsById_cargo_unit_id =
        cargo_unit_id !== null
          ? (allCargoUnitsById.get(cargo_unit_id as t.CargoUnitId) ?? null)
          : null;
      const allOrganizationsById_charterer_organization_id =
        charterer_organization_id !== null
          ? (allOrganizationsById.get(
              charterer_organization_id as t.OrganizationId,
            ) ?? null)
          : null;
      const allHandledBySpotsByOrderId_id =
        allHandledBySpotsByOrderId.get(id as t.OrderId) ?? null;
      const allOrderPortCallRelationsByOrderId_id =
        allOrderPortCallRelationsByOrderId.get(id as t.OrderId) ?? [];
      const allOrderDetailsByOrderId_id =
        allOrderDetailsByOrderId.get(id as t.OrderId) ?? [];
      const allOrderAttributeAssociationsByOrderId_id =
        allOrderAttributeAssociationsByOrderId.get(id as t.OrderId) ?? [];
      const allCargoCompartmentAllocationsByOrderId_id =
        allCargoCompartmentAllocationsByOrderId.get(id as t.OrderId) ?? [];
      const allApiOrdersNoticesByOrderId_id =
        allApiOrdersNoticesByOrderId.get(id as t.OrderId) ?? null;
      const allUsersById_user_id =
        user_id !== null
          ? (allUsersById.get(user_id as t.UserId) ?? null)
          : null;
      const allVesselAttributesById_min_vessel_class_requirement =
        min_vessel_class_requirement !== null
          ? (allVesselAttributesById.get(
              min_vessel_class_requirement as t.VesselAttributeId,
            ) ?? null)
          : null;
      const allCargoExclusionsByOrderId_id =
        allCargoExclusionsByOrderId.get(id as t.OrderId) ?? [];
      return {
        __typename: 'Order' as const,
        archived: archived,
        assortmentId: assortment_id as t.AssortmentId,
        cancelling: cancelling,
        cargoAmount: cargo_amount,
        cargoUnitId: cargo_unit_id as t.CargoUnitId,
        chartererOrganizationId: charterer_organization_id as t.OrganizationId,
        coaId: coa_id as t.CoaContractId,
        completed: completed,
        createdAt: created_at,
        deliveredBefore: delivered_before,
        dischargePortId: discharge_port_id as t.LocationId,
        dischargingOverheadDuration: discharging_overhead_duration,
        dischargingRate: discharging_rate,
        dischargingUnit: mapLoadingUnit(discharging_unit),
        externalOrderId: external_order_id,
        id: id as t.OrderId,
        layday: layday,
        loadPortId: load_port_id as t.LocationId,
        loadingOverheadDuration: loading_overhead_duration,
        loadingRate: loading_rate,
        loadingUnit: mapLoadingUnit(loading_unit),
        marginAmount: margin_amount,
        marginUnit: mapMarginUnit(margin_unit),
        minVesselClassRequirement:
          min_vessel_class_requirement as t.VesselAttributeId,
        nominatedVessel: nominated_vessel,
        rta: rta,
        shipmentId: shipment_id as t.ShipmentId,
        specificGravity: specific_gravity,
        started: started,
        stowageFactorMax: stowage_factor_max,
        stowageFactorMin: stowage_factor_min,
        toBeDeleted: to_be_deleted,
        userId: user_id as t.UserId,
        assortmentByAssortmentId: allAssortmentsById_assortment_id,
        locationByDischargePortId: allLocationsById_discharge_port_id,
        locationByLoadPortId: allLocationsById_load_port_id,
        scheduledOrdersByOrderId: {
          __typename: 'ScheduledOrdersConnection' as const,
          nodes: allScheduledOrdersByOrderId_id,
        },
        orderRequirementAssociationsByOrderId: {
          __typename: 'OrderRequirementAssociationsConnection' as const,
          nodes: allOrderRequirementAssociationsByOrderId_id,
        },
        cargoUnitByCargoUnitId: allCargoUnitsById_cargo_unit_id,
        organizationByChartererOrganizationId:
          allOrganizationsById_charterer_organization_id,
        handledBySpotByOrderId: allHandledBySpotsByOrderId_id,
        orderPortCallRelationsByOrderId: {
          __typename: 'OrderPortCallRelationsConnection' as const,
          nodes: allOrderPortCallRelationsByOrderId_id,
        },
        orderDetailsByOrderId: {
          __typename: 'OrderDetailsConnection' as const,
          nodes: allOrderDetailsByOrderId_id,
        },
        orderAttributeAssociationsByOrderId: {
          __typename: 'OrderAttributeAssociationsConnection' as const,
          nodes: allOrderAttributeAssociationsByOrderId_id,
        },
        cargoCompartmentAllocationsByOrderId: {
          __typename: 'CargoCompartmentAllocationsConnection' as const,
          nodes: allCargoCompartmentAllocationsByOrderId_id,
        },
        apiOrdersNoticesByOrderId: {
          __typename: 'ApiOrdersNoticesConnection' as const,
          nodes: oneOrNone(allApiOrdersNoticesByOrderId_id),
        },
        userByUserId: allUsersById_user_id,
        vesselAttributeByMinVesselClassRequirement:
          allVesselAttributesById_min_vessel_class_requirement,
        cargoExclusionsByOrderId: {
          __typename: 'CargoExclusionsConnection' as const,
          nodes: allCargoExclusionsByOrderId_id,
        },
        outgoingVoyageCharterRelation: ovcrs?.get(true) ?? null,
        incomingVoyageCharterRelation: ovcrs?.get(false) ?? null,
      };
    },
  );
  allOrders.sort((a, b) => a.id - b.id);

  const data: RecursivelyDefinedProps<PlanningPageQuery> = {
    __typename: 'Query' as const,
    allAssortments: {
      __typename: 'AssortmentsConnection' as const,
      nodes: allAssortments,
    },
    allChannelCostEstimates: {
      __typename: 'ChannelCostEstimatesConnection' as const,
      nodes: allChannelCostEstimates,
    },
    allCoaContracts: {
      __typename: 'CoaContractsConnection' as const,
      nodes: allCoaContracts,
    },
    allHandledByCoas: {
      __typename: 'HandledByCoasConnection' as const,
      nodes: allHandledByCoas,
    },
    allHandledBySpots: {
      __typename: 'HandledBySpotsConnection' as const,
      nodes: allHandledBySpots,
    },
    allLatestPortCostByPortAndPrimaryAttributes: {
      __typename: 'LatestPortCostByPortAndPrimaryAttributesConnection' as const,
      nodes: allLatestPortCostByPortAndPrimaryAttributes,
    },
    allOffHires: {
      __typename: 'OffHiresConnection' as const,
      nodes: allOffHires,
    },
    allOrders: {
      __typename: 'OrdersConnection' as const,
      nodes: allOrders,
    },
    allOrganizations: {
      __typename: 'OrganizationsConnection' as const,
      nodes: allOrganizations,
    },
    allPortCalls: {
      __typename: 'PortCallsConnection' as const,
      nodes: allPortCalls,
    },
    allPortCostDefaults: {
      __typename: 'PortCostDefaultsConnection' as const,
      nodes: allPortCostDefaults,
    },
    allPortCostsByPortAndPrimaryAttributes: {
      __typename: 'PortCostsByPortAndPrimaryAttributesConnection' as const,
      nodes: allPortCostsByPortAndPrimaryAttributes,
    },
    allPortCostsByPortAndVesselNames: {
      __typename: 'PortCostsByPortAndVesselNamesConnection' as const,
      nodes: allPortCostsByPortAndVesselNames,
    },
    allScheduledOrders: {
      __typename: 'ScheduledOrdersConnection' as const,
      nodes: allScheduledOrders,
    },
    allShipments: {
      __typename: 'ShipmentsConnection' as const,
      nodes: allShipments,
    },
    allShips: {
      __typename: 'ShipsConnection' as const,
      nodes: allShips,
    },
    allSingleEvents: {
      __typename: 'SingleEventsConnection' as const,
      nodes: allSingleEvents,
    },
    allZones: {
      __typename: 'ZonesConnection' as const,
      nodes: allZones,
    },
    lastCommit: {
      __typename: 'CommitLogsConnection' as const,
      nodes: allCommitLogs,
    },
  };

  if (typeof window !== 'undefined' && window.location.search.includes('dev')) {
    console.log({ data });
  }

  return data;
};
